import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import {
  AlertTriangle, ArrowDownLeft, ArrowUpRight, Ban, BookUser, CalendarClock, Check, CheckCircle2, ChevronDown, ChevronRight, Circle,
  ClipboardCopy, Clock, Download, ExternalLink, Eye, FileText, History, Hourglass, Inbox, Info, Link2,
  ListChecks, Loader2, Mail, MoveDown, MoveUp, Paperclip, PenLine, PhoneCall, Plus, RefreshCw, RotateCcw, Search, Send,
  Settings2, SlidersHorizontal, Smartphone, Trash2, Upload, UserCheck, X,
} from 'lucide-react';
import api from '../../../utils/api';
import {
  PageHeader, Panel, Pill, Banner, Empty, LoadingBlock, ErrorBlock, Button, Tabs, TabPanel, Segmented, Sheet,
  Modal, ReasonModal, ConfirmModal, RowMenu, Field, Input, Textarea, Select, Switch, Checkbox, DataTable, Toolbar, Facts, SearchInput,
  useLoad, downloadFile, errMsg, fmtDate, fmtDateTime, todayIso, words, hoursOf,
} from '../kit';
import { flagText } from './TimeEntryForm';

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Support Coordination → Referrals (SC Workflow Specification v1.0, section 6).
//
// Company level: Received / Sent tabs, a counter strip that is also the status filter (Open,
// Follow-up due, Overdue, Accepted, Declined, Closed, All), search, a Filters sheet for the rest,
// and the list (a table on wide screens, one card per referral on phones).
// Embedded (participant file): the same list for one participant.
// Every record opens in a right-side Sheet; ?referral=<id> deep-links to it (notification links).
// The API decides what can be done (GET /:id → actions); write controls also need Manage referrals.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

const BASE = '/sc/referrals';
const PAGE = 100;
const FINAL = ['closed', 'withdrawn'];
const OPEN_FALLBACK = { received: ['new', 'in_review', 'information_requested', 'waitlisted'], sent: ['draft', 'sent', 'acknowledged'] };
const STATUS_FALLBACK = {
  received: ['new', 'in_review', 'information_requested', 'accepted', 'declined', 'waitlisted', 'closed'],
  sent: ['draft', 'sent', 'acknowledged', 'accepted', 'declined', 'withdrawn', 'closed'],
};
const LABEL_FALLBACK = {
  new: 'New', in_review: 'In review', information_requested: 'Information requested', accepted: 'Accepted', declined: 'Declined',
  waitlisted: 'Waitlisted', closed: 'Closed', draft: 'Draft', sent: 'Sent', acknowledged: 'Acknowledged', withdrawn: 'Withdrawn',
};
const FORM_STATE_FALLBACK = { not_sent: 'Not sent', sent: 'Sent', opened: 'Opened', submitted: 'Submitted', expired: 'Expired', cancelled: 'Cancelled' };
const FORM_NAME_FALLBACK = { referral_response: 'Referral Response', referral_details: 'Referral Details' };
const CLOSE_FALLBACK = ['Service started', 'Accepted by another provider', 'Participant withdrew', 'No response', 'Not eligible', 'Declined', 'Other'];
const VIA = { email: 'Email', phone: 'Phone', online_form: 'Online form', in_person: 'In person' };
const VIA_OPTS = Object.entries(VIA).map(([value, label]) => ({ value, label }));
const PLAN = { ndia: 'NDIA-managed', plan_managed: 'Plan-managed', self_managed: 'Self-managed' };
const PLAN_OPTS = Object.entries(PLAN).map(([value, label]) => ({ value, label }));
const CONSENT_OPTS = [{ value: 'yes', label: 'Yes, consent given' }, { value: 'no', label: 'No' }];
const METHODS = [{ value: 'call', label: 'Call', icon: PhoneCall }, { value: 'email', label: 'Email', icon: Mail }, { value: 'sms', label: 'SMS', icon: Smartphone }];
const METHOD_META = {
  call: ['Call', PhoneCall, 'bg-sky-100 text-sky-700'], email: ['Email', Mail, 'bg-indigo-100 text-indigo-700'],
  sms: ['SMS', Smartphone, 'bg-sky-100 text-sky-700'], form: ['Online form', FileText, 'bg-violet-100 text-violet-700'],
  system: ['Note', Info, 'bg-slate-100 text-slate-600'],
};
const FORM_TONE = { not_sent: 'slate', sent: 'blue', opened: 'blue', submitted: 'violet', expired: 'amber', cancelled: 'slate' };
const FORM_INK = { submitted: 'text-violet-700 font-semibold', expired: 'text-amber-700 font-semibold', sent: 'text-slate-700', opened: 'text-slate-700' };
const DECISION = { accept: ['Accepted', 'emerald'], decline: ['Declined', 'red'], waitlist: ['Waitlisted', 'blue'], need_more_information: ['Needs more information', 'amber'] };
const FIELD_TYPE_LABELS = { text: 'Short text', textarea: 'Long text', email: 'Email', phone: 'Phone', date: 'Date', select: 'Choice', checkbox: 'Tick box', file: 'File upload' };
const AUDIT_LABELS = {
  create: 'Referral created', update: 'Details edited', assign: 'Re-assigned', followup_date: 'Next follow-up date changed',
  status_change: 'Status changed', accept: 'Accepted', email: 'Email sent', followup: 'Follow-up logged', case_note: 'Case note added',
  attachment_add: 'File added', attachment_remove: 'File removed', remove: 'Referral removed', send: 'Form sent', resend: 'Form resent',
  cancel: 'Form link cancelled', reminder: 'Form reminder sent', opened: 'Form opened', submit: 'Form submitted', expire: 'Form link expired',
  form_applied: 'Form answers saved',
};
// The counter strip (spec counters) doubles as the status filter. '' = every status.
const STRIP = [
  { value: 'open', label: 'Open' },
  { value: 'followup_due', label: 'Follow-up due', hot: 'text-amber-700', icon: CalendarClock },
  { value: 'overdue', label: 'Overdue', hot: 'text-rose-700', icon: AlertTriangle },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'closed', label: 'Closed' },
  { value: '', label: 'All' },
];
const STRIP_VALUES = STRIP.map((s) => s.value);
const DURATIONS = [15, 30, 45, 60];
// The activity type a coordinator used last for referral time, so the next one is one tap.
const LAST_TYPE_KEY = 'sc:last-activity-type';
const MAX_FILE = 15 * 1024 * 1024;
const ACCEPT_FILES = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.webp,.heic';
const CONTROL = 'w-full rounded-xl border-0 bg-slate-50 ring-1 ring-inset ring-slate-200 px-3 text-[13.5px] text-slate-900 placeholder:text-slate-500 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white disabled:opacity-60 aria-[invalid=true]:ring-rose-400';
const LINK_BTN = 'inline-flex items-center justify-center gap-1.5 font-semibold rounded-xl transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1';
// The kit Select greys its placeholder with slate-400; this lifts it to readable contrast.
const READABLE_PLACEHOLDER = 'data-[placeholder]:!text-slate-500';

// ── Small helpers ─────────────────────────────────────────────────────────────────────────────
const num = (x) => Number(x) || 0;
const fullName = (a, b) => [a, b].map((x) => String(x || '').trim()).filter(Boolean).join(' ');
const isEmail = (s) => { const v = String(s || '').trim(); return v.length <= 255 && /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(v); };
const isPhone = (s) => { const v = String(s || '').trim(); return v.length <= 30 && /^[0-9+() .-]{6,30}$/.test(v) && v.replace(/\D/g, '').length >= 6; };
const isNdis = (s) => /^\d{9}$/.test(String(s || '').replace(/[\s-]/g, ''));
const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : 'Not recorded');
const fileSize = (b) => { const n = num(b); return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`; };
const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v != null));
const iso10 = (d) => (d ? String(d).slice(0, 10) : '');
const parseJson = (v) => { if (!v) return null; if (typeof v === 'object') return v; try { return JSON.parse(v); } catch { return null; } };
const toMinutes = (t) => { if (!/^\d{1,2}:\d{2}/.test(t || '')) return null; const [h, m] = t.split(':').map(Number); return (h * 60) + m; };
const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const nowMinutes = () => { const d = new Date(); return (d.getHours() * 60) + d.getMinutes(); };
// 19:49 → "7:49 pm", matching how time fields read on screen.
const clock = (m) => { const h = Math.floor(m / 60) % 24; return `${h % 12 || 12}:${String(m % 60).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`; };
const daysText = (n) => `${n} ${n === 1 ? 'day' : 'days'}`;
function readLastType() { try { return window.localStorage.getItem(LAST_TYPE_KEY) || ''; } catch { return ''; } }
function rememberType(id) { try { if (id) window.localStorage.setItem(LAST_TYPE_KEY, id); } catch { /* storage blocked: nothing is remembered */ } }

function addBusinessDays(iso, n) {
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(Date.UTC(y, m - 1, d));
  let left = Math.max(0, num(n));
  while (left > 0) {
    dt.setUTCDate(dt.getUTCDate() + 1);
    const wd = dt.getUTCDay();
    if (wd !== 0 && wd !== 6) left -= 1;
  }
  return dt.toISOString().slice(0, 10);
}

async function copyToClipboard(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

// Emails the API sends alongside an action come back as { sent, message }.
function emailToast(email) {
  if (!email || !email.message) return;
  if (email.sent) toast.success(email.message);
  else toast(email.message, { icon: <AlertTriangle size={16} className="text-amber-600" aria-hidden /> });
}

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// Runs fn whenever rev moves past 0, without making fn's identity a dependency.
function useOnRev(rev, fn) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => { if (rev) ref.current(); }, [rev]);
}

function useScMe() {
  const me = useLoad('/sc/me');
  const perms = me.data?.permissions || [];
  return {
    loaded: !!me.data, userId: me.data?.userId || null,
    canManage: perms.includes('manage_referrals'), canLogTime: perms.includes('log_time'), isAdmin: perms.includes('admin'),
    rounding: num(me.data?.settings?.rounding_minutes) || 1,
  };
}

function useMetaHelpers(meta) {
  return useMemo(() => {
    const m = meta || {};
    const labels = { ...LABEL_FALLBACK, ...(m.status_labels || {}) };
    const open = m.open_statuses || OPEN_FALLBACK;
    return {
      labels, open,
      statuses: m.statuses || STATUS_FALLBACK,
      isOpen: (r) => (open[r.direction] || []).includes(r.status),
      closeOutcomes: m.close_outcomes || CLOSE_FALLBACK,
      followupDays: m.followup_business_days != null ? num(m.followup_business_days) : 2,
      expiryDays: m.form_link_expiry_days || null,
      formStates: { ...FORM_STATE_FALLBACK, ...(m.form_state_labels || {}) },
      formNames: { ...FORM_NAME_FALLBACK, ...(m.form_names || {}) },
    };
  }, [meta]);
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Page
// ═══════════════════════════════════════════════════════════════════════════════════════════════
export default function ScReferrals({ coordinationId, clientId, embedded = false, participantName }) {
  const me = useScMe();
  const metaL = useLoad(`${BASE}/meta`);
  const M = useMetaHelpers(metaL.data);
  const assigneesL = useLoad(`${BASE}/assignees`);
  const assignees = useMemo(() => assigneesL.data || [], [assigneesL.data]);
  const [params, setParams] = useSearchParams();
  const openId = params.get('referral');
  const [rev, setRev] = useState(0);
  const changed = useCallback(() => setRev((n) => n + 1), []);
  const [creating, setCreating] = useState(null);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);

  const openReferral = useCallback((id) => {
    const next = new URLSearchParams(params);
    next.set('referral', id);
    setParams(next);
  }, [params, setParams]);
  const closeReferral = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete('referral');
    setParams(next, { replace: true });
  }, [params, setParams]);
  const created = (ref) => { setCreating(null); changed(); if (ref?.id) openReferral(ref.id); };

  // One primary action; the two settings lists sit behind "…".
  const actions = (
    <>
      {me.canManage && (embedded
        ? <Button icon={Send} onClick={() => setCreating('sent')}>Send a referral</Button>
        : <NewReferralMenu onReceived={() => setCreating('received')} onSent={() => setCreating('sent')} />)}
      <RowMenu label="Referral settings" items={[
        { label: 'Referral contacts', icon: BookUser, onClick: () => setContactsOpen(true) },
        me.isAdmin && !embedded && { label: 'Online form fields', icon: Settings2, onClick: () => setFieldsOpen(true) },
      ]} />
    </>
  );

  return (
    <div>
      {embedded ? (
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h2 className="text-[17px] font-semibold text-slate-900">Referrals</h2>
            <p className="text-[13px] text-slate-500 mt-0.5 max-w-2xl">Referrals sent for this participant, and the one that brought them to you.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        </div>
      ) : (
        <PageHeader title="Referrals" sub="Send, receive and follow up referrals." actions={actions} />
      )}

      {embedded
        ? <EmbeddedList clientId={clientId} coordinationId={coordinationId} M={M} onOpen={openReferral} rev={rev} canManage={me.canManage} onSend={() => setCreating('sent')} />
        : <CompanyList M={M} me={me} assignees={assignees} onOpen={openReferral} rev={rev} onCreate={setCreating} />}

      {openId && <ReferralSheet key={openId} id={openId} onClose={closeReferral} onChanged={changed} me={me} M={M} assignees={assignees} />}
      {creating === 'received' && <NewReceivedSheet onClose={() => setCreating(null)} onCreated={created} assignees={assignees} M={M} />}
      {creating === 'sent' && (
        <NewSentSheet onClose={() => setCreating(null)} onCreated={created} assignees={assignees} M={M}
          preset={embedded ? { clientId, coordinationId, participantName } : null} />
      )}
      {contactsOpen && <ContactsSheet onClose={() => setContactsOpen(false)} canManage={me.canManage} />}
      {fieldsOpen && (
        <Sheet open onClose={() => setFieldsOpen(false)} title="Online form fields" icon={Settings2} width="lg"
          sub="The questions on the Referral Details and Referral Response forms. Changes apply to links sent from now on.">
          <ReferralFormFields canAdmin={me.isAdmin} bare />
        </Sheet>
      )}
    </div>
  );
}

function NewReferralMenu({ onReceived, onSent }) {
  const item = 'flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer outline-none select-none data-[highlighted]:bg-indigo-50';
  return (
    <RMenu.Root>
      <RMenu.Trigger asChild>
        <Button icon={Plus}>New referral<ChevronDown size={14} className="-mr-1 opacity-80" aria-hidden /></Button>
      </RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content align="end" sideOffset={6} className="z-[80] w-[300px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-card-deep ring-1 ring-slate-200/80 p-1 data-[state=open]:animate-scale-in">
          <RMenu.Item className={item} onSelect={onReceived}>
            <span className="mt-0.5 w-8 h-8 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center flex-shrink-0"><ArrowDownLeft size={15} aria-hidden /></span>
            <span><span className="block text-[13px] font-semibold text-slate-900">Log a received referral</span><span className="block text-[12px] text-slate-500">Someone has referred a participant to you.</span></span>
          </RMenu.Item>
          <RMenu.Item className={item} onSelect={onSent}>
            <span className="mt-0.5 w-8 h-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0"><ArrowUpRight size={15} aria-hidden /></span>
            <span><span className="block text-[13px] font-semibold text-slate-900">Send a referral</span><span className="block text-[12px] text-slate-500">Refer a participant to a provider.</span></span>
          </RMenu.Item>
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  );
}

// ── Company list ──────────────────────────────────────────────────────────────────────────────
function CompanyList({ M, me, assignees, onOpen, rev, onCreate }) {
  const [direction, setDirection] = useState('received');
  const [status, setStatus] = useState('open');
  const [assignee, setAssignee] = useState('');
  const [qInput, setQInput] = useState('');
  const q = useDebounced(qInput.trim(), 300);
  const [source, setSource] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const [exporting, setExporting] = useState(false);

  const filters = useMemo(() => clean({
    direction, status, assigned_user_id: assignee, source: direction === 'received' ? source : '', date_from: from, date_to: to, q,
  }), [direction, status, assignee, source, from, to, q]);
  useEffect(() => { setLimit(PAGE); }, [filters]);
  const list = useLoad(BASE, { ...filters, limit });
  const counters = useLoad(`${BASE}/counters`, assignee ? { assigned_user_id: assignee } : {});
  useOnRev(rev, () => { list.reload(); counters.reload(); });

  const c = counters.data;
  const dirCount = (d, k) => num(c?.[d]?.[k]);
  const setDir = (d) => {
    setDirection(d);
    setStatus((s) => (STRIP_VALUES.includes(s) || (M.statuses[d] || []).includes(s) ? s : 'open'));
  };
  const exactStatus = STRIP_VALUES.includes(status) ? '' : status;
  const rows = list.data?.rows || [];
  const total = num(list.data?.total);
  const clearFilters = () => { setAssignee(''); setSource(''); setFrom(''); setTo(''); if (exactStatus) setStatus('open'); };

  // Chips for every filter set in the Filters sheet, so nothing hides the list without saying so.
  const assigneeName = assignee === 'me' ? 'Assigned to me' : assignee === 'unassigned' ? 'Unassigned'
    : `Assigned to ${(assignees.find((a) => a.id === assignee) || {}).name || 'someone'}`;
  const chips = [
    assignee && { key: 'assignee', label: assigneeName, clear: () => setAssignee('') },
    exactStatus && { key: 'status', label: `Status: ${M.labels[exactStatus] || words(exactStatus)}`, clear: () => setStatus('open') },
    direction === 'received' && source && { key: 'source', label: `Received by ${(VIA[source] || source).toLowerCase()}`, clear: () => setSource('') },
    (from || to) && { key: 'dates', label: from && to ? `${fmtDate(from)} to ${fmtDate(to)}` : from ? `From ${fmtDate(from)}` : `Up to ${fmtDate(to)}`, clear: () => { setFrom(''); setTo(''); } },
  ].filter(Boolean);

  const doExport = async () => {
    setExporting(true);
    try { const name = await downloadFile(`${BASE}/export`, { params: filters }); toast.success(`Exported ${name}`); }
    catch (e) { toast.error(errMsg(e, 'Could not export the referrals.')); } finally { setExporting(false); }
  };
  const searching = !!(q || chips.length);
  const statusWord = (STRIP.find((s) => s.value === status) || {}).label;

  return (
    <>
      <Tabs value={direction} onChange={setDir} label="Referral direction" tabs={[
        { value: 'received', label: 'Received', icon: ArrowDownLeft, count: dirCount('received', 'open'), alert: dirCount('received', 'overdue') > 0 },
        { value: 'sent', label: 'Sent', icon: ArrowUpRight, count: dirCount('sent', 'open'), alert: dirCount('sent', 'overdue') > 0 },
      ]}>
        {['received', 'sent'].map((d) => (
          <TabPanel key={d} value={d}>
            <CounterStrip value={exactStatus ? null : status} onChange={setStatus} counts={c ? c[d] : null}
              label={`Show ${d} referrals by status`} />
            <div className="mt-4 mb-3 flex flex-wrap items-center gap-2">
              <ListSearch value={qInput} onChange={setQInput} placeholder="Search number, participant, organisation" className="w-full sm:w-auto sm:flex-1 sm:max-w-sm" />
              <Button variant={chips.length ? 'soft' : 'secondary'} icon={SlidersHorizontal} onClick={() => setFiltersOpen(true)}
                aria-label={chips.length ? `Filters, ${chips.length} on` : 'Filters'}>
                Filters
                {chips.length > 0 && <span className="ml-0.5 min-w-[18px] px-1 rounded-full bg-violet-600 text-white text-[11px] font-bold tabular-nums leading-[18px]" aria-hidden>{chips.length}</span>}
              </Button>
              <Button variant="secondary" icon={Download} loading={exporting} onClick={doExport} className="sm:ml-auto">Export to Excel</Button>
            </div>
            {chips.length > 0 && <FilterChips chips={chips} onClearAll={clearFilters} />}
            <div className="bg-white rounded-2xl border border-slate-200/80 px-2 pt-2 pb-1">
              <ReferralList direction={d} rows={rows} loading={list.loading} error={list.error} onRetry={list.reload} onOpen={onOpen} M={M}
                empty={(
                  <Empty icon={d === 'received' ? Inbox : Send}
                    title={searching ? 'No referrals match' : status === 'open' ? `No open ${d} referrals` : status ? `No ${String(statusWord || '').toLowerCase()} ${d} referrals` : `No ${d} referrals yet`}
                    action={searching
                      ? <Button variant="secondary" icon={X} onClick={() => { setQInput(''); clearFilters(); }}>Clear search and filters</Button>
                      : me.canManage && (status === 'open' || !status)
                        ? <Button icon={Plus} onClick={() => onCreate(d)}>{d === 'received' ? 'Log a received referral' : 'Send a referral'}</Button> : null}>
                    {searching ? 'Try a different search, or clear the filters.' : d === 'received' ? 'Referrals people send you show here until they are closed.' : 'Referrals you send to providers show here until they are closed.'}
                  </Empty>
                )} />
              {rows.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-[12.5px] text-slate-500">
                  <span className="tabular-nums">Showing {rows.length} of {total}</span>
                  {rows.length < total && limit < 1000 && (
                    <Button size="sm" variant="secondary" loading={list.loading} onClick={() => setLimit((n) => Math.min(1000, n + PAGE))}>Show more</Button>
                  )}
                </div>
              )}
            </div>
          </TabPanel>
        ))}
      </Tabs>
      <FiltersSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} direction={direction} M={M} assignees={assignees}
        status={status} setStatus={setStatus} assignee={assignee} setAssignee={setAssignee} source={source} setSource={setSource}
        from={from} setFrom={setFrom} to={to} setTo={setTo} active={chips.length} onClear={clearFilters}
        total={list.loading ? null : total} />
    </>
  );
}

// Search box with a phone-sized tap target (the kit's is desktop height).
function ListSearch({ value, onChange, placeholder, className }) {
  return (
    <div className={clsx('relative', className)}>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" aria-hidden />
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder}
        className={clsx(CONTROL, 'h-11 sm:h-9 pl-9 bg-white')} />
    </div>
  );
}

// The spec's counters as one strip. Each one filters the list; it wraps to two rows on phones so
// every option stays on screen.
function CounterStrip({ value, onChange, counts, label }) {
  return (
    <div role="radiogroup" aria-label={label} className="grid grid-cols-4 sm:grid-cols-7 gap-1 p-1 rounded-2xl bg-slate-100/80">
      {STRIP.map((s) => {
        const on = value === s.value;
        const n = s.value ? num(counts?.[s.value]) : null;
        const hot = s.hot && n > 0;
        return (
          <button key={s.value || 'all'} type="button" role="radio" aria-checked={on} onClick={() => onChange(s.value)}
            aria-label={s.value ? `${s.label}: ${counts ? n : 'loading'}` : 'All statuses'}
            className={clsx('min-h-[56px] rounded-xl px-2 sm:px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
              on ? 'bg-white shadow-sm ring-1 ring-inset ring-slate-200/80' : 'hover:bg-white/70')}>
            <span className={clsx('flex items-center gap-1 text-[18px] leading-6 font-semibold tabular-nums', hot ? s.hot : n ? 'text-slate-900' : 'text-slate-500')} aria-hidden>
              {hot && <s.icon size={14} className="flex-shrink-0" />}
              {!s.value ? <span className="text-slate-700">All</span> : counts ? n : <span className="inline-block h-4 w-5 rounded bg-slate-200 animate-pulse" />}
            </span>
            <span className={clsx('block mt-0.5 text-[12px] leading-tight', on ? 'font-semibold text-slate-900' : 'font-medium text-slate-600')} aria-hidden>
              {s.value ? s.label : 'Any status'}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function FilterChips({ chips, onClearAll }) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" aria-label="Filters on">
      {chips.map((ch) => (
        <button key={ch.key} type="button" onClick={ch.clear} aria-label={`Remove filter: ${ch.label}`}
          className="inline-flex items-center gap-1.5 h-11 sm:h-8 pl-3 pr-2 rounded-full bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-200/70 text-[12.5px] font-semibold hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
          {ch.label}<X size={13} aria-hidden />
        </button>
      ))}
      {chips.length > 1 && <Button size="sm" variant="ghost" onClick={onClearAll}>Clear all</Button>}
    </div>
  );
}

// Everything that isn't the status strip or search: assignee, exact status, source, date range.
function FiltersSheet({ open, onClose, direction, M, assignees, status, setStatus, assignee, setAssignee, source, setSource, from, setFrom, to, setTo, active, onClear, total }) {
  const statusOpts = [
    { value: 'all', label: 'All statuses' }, { value: 'open', label: 'Open' }, { value: 'followup_due', label: 'Follow-up due today' }, { value: 'overdue', label: 'Follow-up overdue' },
    ...(M.statuses[direction] || []).map((s) => ({ value: s, label: M.labels[s] || words(s) })),
  ];
  return (
    <Sheet open={open} onClose={onClose} title="Filters" icon={SlidersHorizontal} sub={direction === 'received' ? 'Received referrals' : 'Sent referrals'}
      footer={(
        <>
          <Button variant="ghost" onClick={onClear} disabled={!active}>Clear filters</Button>
          <Button onClick={onClose}>{total == null ? 'Show referrals' : `Show ${total} ${total === 1 ? 'referral' : 'referrals'}`}</Button>
        </>
      )}>
      <div className="space-y-5">
        <Field label="Assigned to">
          <Select value={assignee} onChange={setAssignee} allowEmpty emptyLabel="Anyone"
            options={[{ value: 'me', label: 'Assigned to me' }, { value: 'unassigned', label: 'Unassigned' }, ...assignees.map((a) => ({ value: a.id, label: a.name, hint: a.email }))]} />
        </Field>
        <Field label="Status">
          <Select value={status || 'all'} onChange={(v) => setStatus(v === 'all' ? '' : v)} options={statusOpts} />
        </Field>
        {direction === 'received' && (
          <Field label="Source"><Select value={source} onChange={setSource} options={VIA_OPTS} allowEmpty emptyLabel="Any source" /></Field>
        )}
        <fieldset>
          <legend className="text-[12.5px] font-semibold text-slate-700 mb-1.5">{direction === 'received' ? 'Date received' : 'Referral date'}</legend>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From"><Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} /></Field>
            <Field label="To"><Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} /></Field>
          </div>
        </fieldset>
      </div>
    </Sheet>
  );
}

// ── Embedded list (participant file) ──────────────────────────────────────────────────────────
// The list is filtered to this participant on the server (client, else engagement).
function EmbeddedList({ clientId, coordinationId, M, onOpen, rev, canManage, onSend }) {
  const who = clientId ? { client_id: clientId } : { coordination_id: coordinationId };
  const rec = useLoad(BASE, { direction: 'received', limit: 1000, ...who });
  const snt = useLoad(BASE, { direction: 'sent', limit: 1000, ...who });
  useOnRev(rev, () => { rec.reload(); snt.reload(); });
  const [direction, setDirection] = useState('sent');
  const [status, setStatus] = useState('');
  const [qInput, setQInput] = useState('');
  const q = useDebounced(qInput.trim().toLowerCase(), 200);

  const lists = useMemo(() => {
    const mine = (rows) => (rows || []).filter((r) => (clientId && r.client_id === clientId) || (coordinationId && r.coordination_id === coordinationId));
    return { received: mine(rec.data?.rows), sent: mine(snt.data?.rows) };
  }, [rec.data, snt.data, clientId, coordinationId]);
  const counts = useMemo(() => {
    const count = (rows) => ({
      open: rows.filter((r) => M.isOpen(r)).length, followup_due: rows.filter((r) => r.followup_due).length,
      overdue: rows.filter((r) => r.overdue).length, accepted: rows.filter((r) => r.status === 'accepted').length,
      declined: rows.filter((r) => r.status === 'declined').length, closed: rows.filter((r) => r.status === 'closed').length,
    });
    return { received: count(lists.received), sent: count(lists.sent) };
  }, [lists, M]);
  const rows = useMemo(() => lists[direction].filter((r) => {
    let ok = true;
    if (status === 'open') ok = M.isOpen(r);
    else if (status === 'followup_due') ok = !!r.followup_due;
    else if (status === 'overdue') ok = !!r.overdue;
    else if (status) ok = r.status === status;
    if (!ok) return false;
    if (!q) return true;
    return [r.referral_number, r.participant, r.organisation, r.referrer_name, r.contact_person, r.service_requested]
      .some((x) => String(x || '').toLowerCase().includes(q));
  }), [lists, direction, status, q, M]);
  const cur = direction === 'received' ? rec : snt;
  const loaded = !!(rec.data && snt.data);
  const capped = num(rec.data?.total) > 1000 || num(snt.data?.total) > 1000;

  return (
    <Tabs value={direction} onChange={setDirection} label="Referral direction" tabs={[
      { value: 'sent', label: 'Sent', icon: ArrowUpRight, count: lists.sent.length, alert: counts.sent.overdue > 0 },
      { value: 'received', label: 'Received', icon: ArrowDownLeft, count: lists.received.length, alert: counts.received.overdue > 0 },
    ]}>
      {['sent', 'received'].map((d) => (
        <TabPanel key={d} value={d}>
          {capped && <div className="mb-3"><Banner tone="amber">Only your company’s latest 1,000 referrals each way were checked for this participant.</Banner></div>}
          {lists[d].length > 0 && (
            <div className="mb-3 space-y-3">
              <CounterStrip value={status} onChange={setStatus} counts={loaded ? counts[d] : null} label={`Show ${d} referrals by status`} />
              {lists[d].length > 5 && <ListSearch value={qInput} onChange={setQInput} placeholder="Search these referrals" className="w-full sm:max-w-xs" />}
            </div>
          )}
          <div className="bg-white rounded-2xl border border-slate-200/80 px-2 pt-2 pb-1">
            <ReferralList direction={d} rows={rows} loading={cur.loading} error={cur.error} onRetry={cur.reload} onOpen={onOpen} M={M} compact
              empty={(
                <Empty icon={d === 'sent' ? Send : Inbox} title={lists[d].length ? 'No referrals match' : d === 'sent' ? 'No referrals sent yet' : 'No received referral linked'}
                  action={lists[d].length
                    ? <Button variant="secondary" onClick={() => { setStatus(''); setQInput(''); }}>Show all referrals</Button>
                    : d === 'sent' && canManage ? <Button icon={Send} onClick={onSend}>Send a referral</Button> : null}>
                  {lists[d].length ? 'Try a different status or search.' : d === 'sent'
                    ? 'Refer this participant to a provider. The provider answers on a secure form and the result lands here.'
                    : 'When a received referral is accepted for this participant it is linked here.'}
                </Empty>
              )} />
          </div>
        </TabPanel>
      ))}
    </Tabs>
  );
}

// ── List: a table on wide screens, a card per referral on phones ──────────────────────────────
function ReferralList({ direction, rows, loading, error, onRetry, onOpen, M, empty, compact }) {
  if (error) return <div className="p-2"><ErrorBlock error={error} onRetry={onRetry} /></div>;
  if (loading && !rows.length) return <div className="px-2"><LoadingBlock rows={5} /></div>;
  if (!rows.length) return empty;
  return (
    <>
      <div className="hidden sm:block">
        <ReferralTable direction={direction} rows={rows} onOpen={onOpen} M={M} compact={compact} />
      </div>
      <ul className="sm:hidden divide-y divide-slate-100" aria-label={`${direction === 'received' ? 'Received' : 'Sent'} referrals`}>
        {rows.map((r) => <ReferralCard key={r.id} r={r} M={M} compact={compact} onOpen={onOpen} />)}
      </ul>
    </>
  );
}

function FollowUpCell({ row, M }) {
  const d = row.next_followup_date;
  if (!d) return <span className="text-slate-500">Not set</span>;
  if (row.overdue) {
    return (
      <span className="inline-flex flex-col leading-tight text-rose-700">
        <span className="inline-flex items-center gap-1 font-semibold whitespace-nowrap"><AlertTriangle size={13} aria-hidden />{fmtDate(d)}</span>
        <span className="text-[12px] font-medium">{daysText(num(row.days_overdue))} overdue</span>
      </span>
    );
  }
  if (row.followup_due) return <span className="inline-flex items-center gap-1 font-semibold text-amber-700 whitespace-nowrap"><CalendarClock size={13} aria-hidden />Due today</span>;
  return <span className={clsx('tabular-nums whitespace-nowrap', M.isOpen(row) ? 'text-slate-700' : 'text-slate-500')}>{fmtDate(d)}</span>;
}

function FormStatus({ row, M }) {
  const s = row.form_status || 'not_sent';
  return <span className={clsx('whitespace-nowrap', FORM_INK[s] || 'text-slate-500')}>{row.form_status_label || M.formStates[s] || words(s)}</span>;
}

// Spec columns: number, participant, organisation, service requested, date, status, assigned to,
// next follow-up, form status.
function ReferralTable({ direction, rows, onOpen, M, compact }) {
  const columns = useMemo(() => [
    {
      accessorKey: 'referral_number', header: 'Number', meta: { width: 104 },
      cell: ({ row }) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(row.original.id); }}
          className="font-mono text-[12.5px] font-semibold text-violet-700 hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 whitespace-nowrap">
          {row.original.referral_number || 'Open'}
        </button>
      ),
    },
    ...(compact ? [] : [{ accessorKey: 'participant', header: 'Participant', cell: ({ getValue }) => <span className="font-semibold text-slate-900">{getValue() || '—'}</span> }]),
    {
      accessorKey: 'organisation', header: 'Organisation',
      cell: ({ row }) => {
        const r = row.original;
        const person = direction === 'received' ? r.referrer_name : r.contact_person;
        return (
          <div className="min-w-0 max-w-[200px]">
            <p className="truncate text-slate-800">{r.organisation || person || '—'}</p>
            {r.organisation && person && <p className="truncate text-[12px] text-slate-500">{person}</p>}
          </div>
        );
      },
    },
    {
      accessorKey: 'service_requested', header: () => <span className="whitespace-nowrap">Service requested</span>,
      cell: ({ getValue }) => <p className="max-w-[170px] truncate text-slate-600" title={getValue() || undefined}>{getValue() || '—'}</p>,
    },
    { accessorKey: 'referral_date', header: direction === 'received' ? 'Received' : 'Date', cell: ({ getValue }) => <span className="tabular-nums whitespace-nowrap">{fmtDate(getValue())}</span> },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <Pill status={row.original.status} label={row.original.status_label || M.labels[row.original.status]} /> },
    { accessorKey: 'assigned_to', header: () => <span className="whitespace-nowrap">Assigned to</span>, cell: ({ getValue }) => getValue() || <span className="text-amber-700 font-medium">Unassigned</span> },
    { accessorKey: 'next_followup_date', header: () => <span className="whitespace-nowrap">Next follow-up</span>, cell: ({ row }) => <FollowUpCell row={row.original} M={M} /> },
    { accessorKey: 'form_status', header: () => <span className="whitespace-nowrap">Form status</span>, cell: ({ row }) => <FormStatus row={row.original} M={M} /> },
  ], [direction, onOpen, M, compact]);
  return (
    <DataTable columns={columns} data={rows} dense
      onRowClick={(r) => onOpen(r.id)} rowClassName={(r) => (r.overdue ? 'bg-rose-50/40' : undefined)} />
  );
}

function ReferralCard({ r, M, compact, onOpen }) {
  const person = r.direction === 'received' ? r.referrer_name : r.contact_person;
  const org = [r.organisation, person].filter(Boolean).join(' · ');
  const n = num(r.days_overdue);
  return (
    <li>
      <button type="button" onClick={() => onOpen(r.id)}
        className={clsx('w-full flex items-start gap-3 text-left px-3 py-3 rounded-xl transition-colors hover:bg-indigo-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
          r.overdue && 'bg-rose-50/40')}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-[12.5px] font-semibold text-violet-700">{r.referral_number}</span>
            <Pill status={r.status} label={r.status_label || M.labels[r.status]} />
          </div>
          {!compact && <p className="mt-1 text-[14px] font-semibold text-slate-900 truncate">{r.participant || 'Participant not named'}</p>}
          {org && <p className={clsx('text-[13px] text-slate-700 truncate', compact && 'mt-1')}>{org}</p>}
          {r.service_requested && <p className="text-[12.5px] text-slate-500 truncate">{r.service_requested}</p>}
          <p className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px]">
            {r.overdue ? (
              <span className="inline-flex items-center gap-1 font-semibold text-rose-700"><AlertTriangle size={13} aria-hidden />Follow-up {daysText(n)} overdue</span>
            ) : r.followup_due ? (
              <span className="inline-flex items-center gap-1 font-semibold text-amber-700"><CalendarClock size={13} aria-hidden />Follow-up due today</span>
            ) : r.next_followup_date ? <span className="text-slate-600">Follow-up {fmtDate(r.next_followup_date)}</span> : null}
            <span className={r.assigned_to ? 'text-slate-600' : 'text-amber-700 font-medium'}>{r.assigned_to || 'Unassigned'}</span>
            <span className="text-slate-500">{r.direction === 'received' ? 'Received' : 'Dated'} {fmtDate(r.referral_date)}</span>
            {r.form_status && r.form_status !== 'not_sent' && <span className={FORM_INK[r.form_status] || 'text-slate-500'}>Form {String(r.form_status_label || M.formStates[r.form_status] || '').toLowerCase()}</span>}
          </p>
        </div>
        <ChevronRight size={16} className="mt-1 text-slate-500 flex-shrink-0" aria-hidden />
      </button>
    </li>
  );
}

// A select with a label only screen readers hear.
let lblSeq = 0;
function LabelledSelect({ label, className, children }) {
  const [id] = useState(() => `ref-sel-${++lblSeq}`);
  return (
    <div className={className}>
      <label htmlFor={id} className="sr-only">{label}</label>
      {children(id)}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Detail Sheet
// ═══════════════════════════════════════════════════════════════════════════════════════════════
function ReferralSheet({ id, onClose, onChanged, me, M, assignees }) {
  const d = useLoad(`${BASE}/${id}`);
  const reloadDetail = d.reload;
  const refresh = useCallback(() => { reloadDetail(); onChanged(); }, [reloadDetail, onChanged]);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [accepted, setAccepted] = useState(null);
  const [busy, setBusy] = useState('');
  const [copyText, setCopyText] = useState(null);

  const data = d.data;
  const r = data?.referral;
  const actions = data?.actions || [];
  const has = (a) => actions.includes(a);
  const received = r?.direction === 'received';
  const url = `${BASE}/${id}`;
  const sid = (s) => `ref-${id}-${s}`;
  const scrollTo = (s) => { const el = document.getElementById(sid(s)); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const quick = async (key, fn, success) => {
    setBusy(key);
    try {
      const res = await fn();
      if (success) toast.success(success);
      emailToast(res?.data?.email);
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };
  const copyDetails = async () => {
    setBusy('copy');
    try {
      const res = await api.get(`${url}/copy-text`);
      const text = res.data?.text || '';
      if (await copyToClipboard(text)) toast.success('Referral details copied. Paste them into the provider’s form.');
      else setCopyText(text);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };
  const openLog = () => { setLogOpen(true); setTimeout(() => scrollTo('followups'), 30); };

  const title = r ? fullName(r.referral_number, (r.participant_first_name || r.participant_last_name) ? `· ${fullName(r.participant_first_name, r.participant_last_name)}` : '') : 'Referral';
  const sub = !r ? undefined : received
    ? `Received referral · ${fmtDate(r.referral_date)}${r.referrer_org || r.referrer_name ? ` · from ${r.referrer_org || r.referrer_name}` : ''}`
    : `Sent referral · ${fmtDate(r.referral_date)}${r.receiving_org ? ` · to ${r.receiving_org}` : ''}`;

  let body;
  if (d.error && !data) body = <ErrorBlock error={d.error} onRetry={d.reload} />;
  else if (!r) body = <LoadingBlock rows={6} />;
  else if (editing) {
    body = <EditReferral r={r} M={M} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} />;
  } else {
    const final = FINAL.includes(r.status);
    const open = M.isOpen(r);
    const draft = r.status === 'draft';
    // One primary action for the status, "Log follow-up" beside it, everything else under "…".
    const followAct = has('followup') ? { key: 'followup', label: 'Log follow-up', icon: PhoneCall, onClick: openLog } : null;
    const closeAct = has('close') ? { key: 'close', label: 'Close with outcome', icon: CheckCircle2, onClick: () => setModal('close') } : null;
    let primary = null;
    if (received && has('accept')) primary = { key: 'accept', label: 'Accept referral', icon: UserCheck, onClick: () => setModal('accept') };
    else if (!received && has('send') && draft) primary = { key: 'send', label: 'Send referral', icon: Send, onClick: () => setModal('send') };
    else if (closeAct && !open) primary = closeAct;
    else primary = followAct || closeAct;
    const secondary = primary === followAct ? null : followAct;
    const more = [];
    if (received) {
      if (has('review') && r.status !== 'in_review') more.push({ label: 'Mark in review', icon: Eye, onClick: () => quick('review', () => api.post(`${url}/review`), 'Marked in review') });
      if (has('request_info')) more.push({ label: 'Request more information', icon: Mail, onClick: () => setModal('request_info') });
      if (has('waitlist')) more.push({ label: 'Waitlist', icon: Hourglass, onClick: () => setModal('waitlist') });
      if (has('decline')) more.push({ label: 'Decline referral', icon: Ban, onClick: () => setModal('decline') });
      if (has('send_form')) more.push({ label: 'Send Referral Details form', icon: FileText, onClick: () => setModal('send_form') });
      if (me.canManage && !final) more.push({ label: r.referrer_email ? 'Email acknowledgement' : 'Email acknowledgement (no referrer email)', icon: Mail, disabled: !r.referrer_email, onClick: () => quick('ack', () => api.post(`${url}/acknowledge`)) });
    } else {
      if (has('mark_sent') && draft) more.push({ label: 'Mark as sent another way', icon: Check, onClick: () => setModal('mark_sent') });
      if (has('send') && !draft) more.push({ label: 'Send the referral again', icon: Send, onClick: () => setModal('send') });
      if (has('send_form')) more.push({ label: 'Send Referral Response form', icon: FileText, onClick: () => setModal('send_form') });
    }
    if (closeAct && primary !== closeAct) more.push(closeAct);
    if (has('edit')) more.push({ label: 'Edit details', icon: PenLine, onClick: () => setEditing(true) });
    more.push({ label: 'Copy referral details', icon: ClipboardCopy, onClick: copyDetails });
    const danger = [];
    if (has('withdraw')) danger.push({ label: 'Withdraw referral', icon: Ban, danger: true, onClick: () => setModal('withdraw') });
    if (me.canManage && ['new', 'draft'].includes(r.status)) danger.push({ label: 'Remove referral', icon: Trash2, danger: true, onClick: () => setModal('remove') });
    if (danger.length) more.push('sep', ...danger);

    const followups = data.followups || [];
    const forms = data.forms || [];
    const attachments = data.attachments || [];
    const audit = data.audit || [];
    const sections = [
      { id: 'details', label: 'Details' },
      { id: 'followups', label: 'Follow-ups', count: followups.length },
      { id: 'forms', label: 'Online forms', count: forms.length },
      { id: 'files', label: 'Attachments', count: attachments.length },
      { id: 'history', label: 'History', count: audit.length },
    ];

    body = (
      <div>
        <ReferralHeader r={r} M={M} me={me} assignees={assignees} onChanged={refresh} />

        <div className="mt-4 flex flex-wrap items-center gap-2" role="group" aria-label="Referral actions">
          {primary && <Button size="sm" icon={primary.icon} loading={busy === primary.key} onClick={primary.onClick}>{primary.label}</Button>}
          {secondary && <Button size="sm" variant="secondary" icon={secondary.icon} loading={busy === secondary.key} onClick={secondary.onClick}>{secondary.label}</Button>}
          <RowMenu items={more} label="More referral actions" />
          {['copy', 'ack', 'review'].includes(busy) && <Loader2 size={15} className="animate-spin text-slate-500" aria-label="Working" />}
        </div>

        <div className="mt-4 space-y-3 empty:hidden">
          {!received && !final && r.consent_to_share !== true && (
            <Banner tone="amber" title="Consent not confirmed"
              action={has('edit') && <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Record consent</Button>}>
              The referral can’t be sent, copied or followed up by form until the participant’s consent to share their information is recorded.
            </Banner>
          )}
          {received && r.status === 'information_requested' && (
            <Banner tone="blue" title="Waiting for the referrer">The Referral Details form has been sent. When they submit it, the answers save here and the referral moves back to In review.</Banner>
          )}
          {received && r.status === 'accepted' && data.checklist && <SetupChecklist checklist={data.checklist} coordinationId={r.coordination_id} />}
          {r.coordination_id && !(received && r.status === 'accepted') && (
            <p className="text-[12.5px] text-slate-600 flex flex-wrap items-center gap-1.5">
              <Link2 size={13} aria-hidden /> Linked to the participant’s file.
              <Link to={`/support-coordination/${r.coordination_id}`} className="font-semibold text-indigo-700 hover:underline">Open participant file</Link>
            </p>
          )}
        </div>

        {/* Jump links for long referrals on wider screens; on a phone the page just scrolls. */}
        <nav aria-label="Sections of this referral" className="mt-5 hidden sm:flex flex-wrap gap-1.5">
          {sections.map((s) => (
            <button key={s.id} type="button" onClick={() => scrollTo(s.id)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12.5px] font-semibold bg-slate-100 text-slate-700 hover:bg-violet-50 hover:text-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
              {s.label}{s.count > 0 && <span className="tabular-nums text-[11px] font-bold text-slate-500">{s.count}</span>}
            </button>
          ))}
        </nav>

        <Section id={sid('details')} title="Details" icon={FileText}>
          {received ? <ReceivedDetails r={r} /> : <SentDetails r={r} client={data.client} />}
        </Section>

        <Section id={sid('followups')} title="Follow-ups" icon={PhoneCall} count={followups.length}
          actions={has('followup') && !logOpen && <Button size="sm" variant="soft" icon={Plus} onClick={() => setLogOpen(true)}>Log follow-up</Button>}>
          <FollowUps r={r} M={M} followups={followups} logOpen={logOpen} onCloseLog={() => setLogOpen(false)} onChanged={refresh}
            canCase={me.canManage && me.canLogTime} rounding={me.rounding} />
        </Section>

        <Section id={sid('forms')} title="Online forms" icon={Link2} count={forms.length}
          actions={has('send_form') && <Button size="sm" variant="soft" icon={Send} onClick={() => setModal('send_form')}>Send form</Button>}>
          <OnlineForms r={r} M={M} forms={forms} canManage={me.canManage} onChanged={refresh} />
        </Section>

        <Section id={sid('files')} title="Attachments" icon={Paperclip} count={attachments.length}>
          <Attachments r={r} attachments={attachments} canManage={me.canManage} onChanged={refresh} />
        </Section>

        <Section id={sid('history')} title="History" icon={History} count={audit.length}>
          <AuditTrail audit={audit} M={M} />
        </Section>
      </div>
    );
  }

  const done = () => { setModal(null); refresh(); };
  return (
    <Sheet open onClose={onClose} width="lg" icon={r && !received ? ArrowUpRight : ArrowDownLeft} title={title} sub={sub}>
      {body}
      {r && modal === 'accept' && <AcceptModal r={r} assignees={assignees} onClose={() => setModal(null)} onDone={(res) => { setModal(null); setAccepted(res); refresh(); }} />}
      {accepted && <AcceptedModal result={accepted} onClose={() => setAccepted(null)} />}
      {r && modal === 'decline' && <DeclineModal r={r} onClose={() => setModal(null)} onDone={done} />}
      {r && modal === 'waitlist' && <WaitlistModal r={r} onClose={() => setModal(null)} onDone={done} />}
      {r && (modal === 'request_info' || modal === 'send_form') && <SendFormModal r={r} M={M} mode={modal} onClose={() => setModal(null)} onDone={done} />}
      {r && modal === 'send' && <SendReferralModal r={r} M={M} attachments={data?.attachments || []} onClose={() => setModal(null)} onDone={done} onEdit={() => { setModal(null); setEditing(true); }} />}
      {r && modal === 'mark_sent' && <MarkSentModal r={r} onClose={() => setModal(null)} onDone={done} />}
      {r && modal === 'withdraw' && <WithdrawModal r={r} onClose={() => setModal(null)} onDone={done} />}
      {r && modal === 'close' && <CloseModal r={r} M={M} onClose={() => setModal(null)} onDone={done} />}
      {r && modal === 'remove' && <RemoveReferralModal r={r} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); onClose(); }} />}
      {copyText != null && <CopyTextModal text={copyText} onClose={() => setCopyText(null)} />}
    </Sheet>
  );
}

function Section({ id, title, icon: Icon, count, actions, children }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="scroll-mt-4 pt-6 mt-6 border-t border-slate-100">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 id={`${id}-h`} className="flex items-center gap-2 text-[14px] font-semibold text-slate-900">
          {Icon && <span className="w-7 h-7 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center"><Icon size={14} aria-hidden /></span>}
          {title}
          {count > 0 && <span className="text-[11px] font-bold tabular-nums text-slate-500">{count}</span>}
        </h3>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

// Status, assignee, next follow-up and the date: the four facts a coordinator scans first.
let hdrSeq = 0;
function ReferralHeader({ r, M, me, assignees, onChanged }) {
  const [ids] = useState(() => { hdrSeq += 1; return { assign: `ref-assign-${hdrSeq}`, next: `ref-next-${hdrSeq}` }; });
  const [assigning, setAssigning] = useState(false);
  const final = FINAL.includes(r.status);
  const canEdit = me.canManage && !final;
  const opts = assignees.map((a) => ({ value: a.id, label: a.name, hint: a.email }));
  if (r.assigned_user_id && !opts.some((o) => o.value === r.assigned_user_id)) {
    opts.unshift({ value: r.assigned_user_id, label: r.assigned_to || 'Former user', hint: 'Can’t work referrals any more' });
  }
  const assign = async (uid) => {
    if (!uid || uid === r.assigned_user_id) return;
    setAssigning(true);
    try { await api.put(`${BASE}/${r.id}/assign`, { assigned_user_id: uid }); toast.success('Referral re-assigned'); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setAssigning(false); }
  };
  const label = 'block text-[12px] font-medium text-slate-600 mb-1';
  return (
    <div className="rounded-2xl bg-slate-50/70 ring-1 ring-inset ring-slate-200/70 p-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="min-w-0">
        <p className={label}>Status</p>
        <div className="flex flex-wrap items-center gap-1.5">
          <Pill status={r.status} label={r.status_label || M.labels[r.status]} />
          {r.form_status && r.form_status !== 'not_sent' && <Pill tone={FORM_TONE[r.form_status]} label={`Form ${String(M.formStates[r.form_status] || r.form_status).toLowerCase()}`} />}
        </div>
        {r.outcome && <p className="mt-1 text-[12px] text-slate-600">Outcome: {r.outcome}</p>}
      </div>
      <div className="min-w-0">
        <label htmlFor={ids.assign} className={label}>Assigned to</label>
        {canEdit ? (
          <div className="flex items-center gap-1.5">
            <Select id={ids.assign} value={r.assigned_user_id || ''} onChange={assign} options={opts} placeholder="Unassigned" disabled={assigning}
              className={clsx('bg-white', READABLE_PLACEHOLDER)} />
            {assigning && <Loader2 size={14} className="animate-spin text-slate-500 flex-shrink-0" aria-label="Saving" />}
          </div>
        ) : <p id={ids.assign} className="text-[13.5px] text-slate-900">{r.assigned_to || <span className="text-amber-700">Unassigned</span>}</p>}
      </div>
      <div className="min-w-0">
        <label htmlFor={ids.next} className={label}>Next follow-up</label>
        <NextFollowUp r={r} inputId={ids.next} canEdit={canEdit} onChanged={onChanged} />
      </div>
      <div>
        <p className={label}>{r.direction === 'received' ? 'Received' : 'Sent'}</p>
        {r.direction === 'received' ? (
          <p className="text-[13.5px] text-slate-900">{fmtDate(r.referral_date)}{r.received_via && <span className="text-slate-500"> · {VIA[r.received_via] || words(r.received_via)}</span>}</p>
        ) : r.sent_at ? (
          <p className="text-[13.5px] text-slate-900">{fmtDateTime(r.sent_at)}<span className="text-slate-500"> · {r.sent_method === 'email' ? 'by email' : 'another way'}</span></p>
        ) : <p className="text-[13.5px] text-slate-500">Not sent yet</p>}
      </div>
    </div>
  );
}

function NextFollowUp({ r, inputId, canEdit, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(iso10(r.next_followup_date));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(iso10(r.next_followup_date)); }, [r.next_followup_date]);
  const save = async (value) => {
    setBusy(true);
    try {
      await api.put(`${BASE}/${r.id}/next-followup`, { next_followup_date: value || null });
      toast.success(value ? `Next follow-up set for ${fmtDate(value)}` : 'Next follow-up cleared');
      setEditing(false); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Input id={inputId} type="date" value={val} onChange={(e) => setVal(e.target.value)} className="h-11 sm:h-9 w-[150px] bg-white" />
        <Button size="sm" loading={busy} onClick={() => save(val)}>Save date</Button>
        <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setVal(iso10(r.next_followup_date)); }}>Cancel</Button>
        {r.next_followup_date && <Button size="sm" variant="ghost" onClick={() => save('')}>Clear date</Button>}
      </div>
    );
  }
  const d = r.next_followup_date;
  return (
    <div className="flex items-start gap-1">
      <span id={inputId} className={clsx('text-[13.5px]', r.overdue ? 'text-rose-700 font-semibold' : r.due_today ? 'text-amber-700 font-semibold' : d ? 'text-slate-900' : 'text-slate-500')}>
        <span className="inline-flex items-center gap-1">
          {r.overdue && <AlertTriangle size={13} aria-hidden />}{r.due_today && <CalendarClock size={13} aria-hidden />}
          {d ? fmtDate(d) : 'Not set'}
        </span>
        {r.overdue && <span className="block text-[12px] font-medium">{daysText(num(r.days_overdue))} overdue</span>}
        {r.due_today && <span className="block text-[12px] font-medium">Due today</span>}
      </span>
      {canEdit && (
        <button type="button" onClick={() => setEditing(true)} aria-label="Change the next follow-up date"
          className="-mt-1.5 p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
          <PenLine size={14} aria-hidden />
        </button>
      )}
    </div>
  );
}

// ── Details ───────────────────────────────────────────────────────────────────────────────────
function SubHead({ children }) {
  return <h4 className="text-[13px] font-semibold text-slate-900 mb-2">{children}</h4>;
}
function TextBlock({ label, value, tone: t }) {
  return (
    <div className={clsx('rounded-xl px-3.5 py-2.5', t === 'amber' && value ? 'bg-amber-50/70 ring-1 ring-inset ring-amber-100' : 'bg-slate-50')}>
      <p className={clsx('text-[12px] font-medium', t === 'amber' && value ? 'text-amber-800' : 'text-slate-600')}>{label}</p>
      <p className={clsx('mt-0.5 text-[13.5px] whitespace-pre-wrap break-words', value ? 'text-slate-900' : 'text-slate-500')}>{value || 'Not recorded'}</p>
    </div>
  );
}
const contactLink = (kind, v) => (v ? <a href={`${kind}:${v}`} className="text-indigo-700 hover:underline break-all">{v}</a> : null);

function ReceivedDetails({ r }) {
  return (
    <div className="space-y-5">
      <div>
        <SubHead>Referrer</SubHead>
        <Facts cols={3} items={[
          ['Name', r.referrer_name], ['Organisation', r.referrer_org], ['Role', r.referrer_role],
          ['Phone', contactLink('tel', r.referrer_phone)], ['Email', contactLink('mailto', r.referrer_email)],
        ]} />
      </div>
      <div>
        <SubHead>Participant</SubHead>
        <Facts cols={3} items={[
          ['Name', fullName(r.participant_first_name, r.participant_last_name) || null], ['NDIS number', r.ndis_number], ['Date of birth', r.date_of_birth ? fmtDate(r.date_of_birth) : null],
          ['Phone', contactLink('tel', r.participant_phone)], ['Email', contactLink('mailto', r.participant_email)], ['Plan management', PLAN[r.plan_management_type] || (r.plan_management_type ? words(r.plan_management_type) : 'Not sure')],
          ['Plan dates', r.plan_start || r.plan_end ? `${fmtDate(r.plan_start)} to ${fmtDate(r.plan_end)}` : null],
          ['Address', r.participant_address],
        ]} />
      </div>
      <div className="space-y-2">
        <SubHead>Request</SubHead>
        <TextBlock label="Services requested" value={r.services_requested} />
        <TextBlock label="Summary of support needs" value={r.support_needs} />
        <TextBlock label="Known risks" value={r.known_risks} tone="amber" />
      </div>
      <ConsentAndOutcome r={r} />
    </div>
  );
}

function SentDetails({ r, client }) {
  const response = parseJson(r.response);
  const dec = DECISION[r.provider_decision];
  return (
    <div className="space-y-5">
      <div>
        <SubHead>Participant</SubHead>
        <Facts cols={3} items={[
          ['Name', fullName(r.participant_first_name, r.participant_last_name) || fullName(client?.first_name, client?.last_name) || null],
          ['NDIS number', r.ndis_number || client?.ndis_number], ['Date of birth', r.date_of_birth ? fmtDate(r.date_of_birth) : null],
        ]} />
      </div>
      <div>
        <SubHead>Receiving organisation</SubHead>
        <Facts cols={3} items={[
          ['Organisation', r.receiving_org], ['Contact person', r.contact_person], ['Email', contactLink('mailto', r.contact_email)], ['Phone', contactLink('tel', r.contact_phone)],
        ]} />
      </div>
      <div className="space-y-2">
        <SubHead>Request</SubHead>
        <TextBlock label="Service requested" value={r.services_requested} />
        <TextBlock label="Reason for referral" value={r.reason} />
        <TextBlock label="Support needs" value={r.support_needs} />
        <TextBlock label="Known risks" value={r.known_risks} tone="amber" />
      </div>
      {(r.provider_decision || response) && (
        <div className="rounded-2xl ring-1 ring-inset ring-violet-200/70 bg-violet-50/40 p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <p className="text-[13.5px] font-semibold text-slate-900">Provider’s response</p>
            {dec && <Pill tone={dec[1]} label={dec[0]} />}
            {r.proposed_start_date && <span className="text-[12.5px] text-slate-600">Proposed start {fmtDate(r.proposed_start_date)}</span>}
          </div>
          {response ? <AnswerList answers={response} formType="referral_response" /> : <p className="text-[13px] text-slate-500">No answers saved.</p>}
        </div>
      )}
      <ConsentAndOutcome r={r} />
    </div>
  );
}

function ConsentAndOutcome({ r }) {
  return (
    <div>
      <SubHead>Consent and outcome</SubHead>
      <Facts cols={3} items={[
        ['Consent to share information', (
          <span key="c" className={clsx('inline-flex items-center gap-1 font-semibold', r.consent_to_share === true ? 'text-emerald-700' : r.consent_to_share === false ? 'text-rose-600' : 'text-amber-700')}>
            {r.consent_to_share === true ? <CheckCircle2 size={14} aria-hidden /> : <AlertTriangle size={14} aria-hidden />}
            {yesNo(r.consent_to_share)}{r.consent_date ? ` · ${fmtDate(r.consent_date)}` : ''}
          </span>
        )],
        r.outcome && ['Outcome', r.outcome],
        r.decline_reason && ['Reason declined', r.decline_reason],
        r.outcome_note && ['Outcome note', r.outcome_note],
        r.closed_at && ['Closed', `${fmtDateTime(r.closed_at)}${r.closed_by_name ? ` by ${r.closed_by_name}` : ''}`],
        ['Created', `${fmtDateTime(r.created_at)}${r.created_by_name ? ` by ${r.created_by_name}` : ''}`],
      ]} />
    </div>
  );
}

// Form answers, labelled from the company's form fields where they are known.
function AnswerList({ answers, formType }) {
  const l = useLoad(`${BASE}/form-fields`);
  const fieldMap = useMemo(() => {
    const f = l.data?.forms?.[formType];
    const map = {};
    [...(f?.defaults || []), ...(f?.fields || [])].forEach((x) => { map[x.key] = x; });
    return map;
  }, [l.data, formType]);
  const order = Object.keys(fieldMap);
  const entries = Object.entries(answers || {}).sort(([a], [b]) => {
    const ia = order.indexOf(a); const ib = order.indexOf(b);
    return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
  });
  const show = (k, v) => {
    if (v == null || v === '') return <span className="text-slate-500">—</span>;
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (Array.isArray(v)) return v.length ? v.join(', ') : <span className="text-slate-500">None</span>;
    if (typeof v === 'number') return fieldMap[k]?.type === 'file' ? `${v} ${v === 1 ? 'file' : 'files'}` : String(v);
    const s = String(v);
    const opt = (fieldMap[k]?.options || []).find((o) => o.value === s);
    if (opt) return opt.label;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return fmtDate(s);
    return s;
  };
  if (!entries.length) return <p className="text-[13px] text-slate-500">No answers.</p>;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
      {entries.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[12px] font-medium text-slate-500">{fieldMap[k]?.label || words(k)}</dt>
          <dd className="mt-0.5 text-[13px] text-slate-900 whitespace-pre-wrap break-words">{show(k, v)}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── Client setup checklist (6.6) ──────────────────────────────────────────────────────────────
function SetupChecklist({ checklist, coordinationId, onNavigate }) {
  const items = checklist?.items || [];
  const done = items.filter((i) => i.done).length;
  return (
    <div className="rounded-2xl ring-1 ring-inset ring-emerald-200/80 bg-emerald-50/40 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-slate-900 flex items-center gap-2"><ListChecks size={15} className="text-emerald-700" aria-hidden />Setup checklist</p>
          <p className="text-[12.5px] text-slate-600 mt-0.5">Time can be logged straight away. It can’t be claimed until every item is done.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={checklist?.complete ? 'emerald' : 'amber'} label={checklist?.complete ? 'Complete' : `${done} of ${items.length} done`} />
          {coordinationId && (
            <Link to={`/support-coordination/${coordinationId}`} onClick={onNavigate}
              className={clsx(LINK_BTN, 'text-[12.5px] h-8 px-3 bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50')}>
              Open participant file<ExternalLink size={13} aria-hidden />
            </Link>
          )}
        </div>
      </div>
      <ul className="mt-3 divide-y divide-emerald-100">
        {items.map((i) => (
          <li key={i.key} className="flex items-start gap-3 py-2">
            {i.done ? <CheckCircle2 size={16} className="mt-0.5 text-emerald-600 flex-shrink-0" aria-hidden /> : <Circle size={16} className="mt-0.5 text-amber-500 flex-shrink-0" aria-hidden />}
            <div className="min-w-0">
              <p className={clsx('text-[13px] font-medium', i.done ? 'text-slate-700' : 'text-slate-900')}>
                {i.label}<span className="sr-only">{i.done ? ' (done)' : ' (still to do)'}</span>
              </p>
              <p className="text-[12px] text-slate-500">Needed for {String(i.neededFor || '').toLowerCase()}{!i.done && i.detail ? ` · ${i.detail}` : ''}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Follow-ups (6.8) ──────────────────────────────────────────────────────────────────────────
function FollowUps({ r, M, followups, logOpen, onCloseLog, onChanged, canCase, rounding }) {
  const [caseFor, setCaseFor] = useState(null);
  return (
    <div className="space-y-3">
      {logOpen && <LogFollowUpForm r={r} M={M} canCase={canCase} rounding={rounding} onCancel={onCloseLog} onSaved={() => { onCloseLog(); onChanged(); }} />}
      {!followups.length && !logOpen && <p className="text-[13px] text-slate-500 rounded-xl bg-slate-50 px-4 py-3">No follow-ups yet. Each call, email or SMS you log shows here with the next follow-up date.</p>}
      {followups.length > 0 && (
        <ol className="space-y-2.5">
          {followups.map((fu) => {
            const [label, Icon, cls] = METHOD_META[fu.method] || [words(fu.method || 'note'), Info, 'bg-slate-100 text-slate-600'];
            const canMake = canCase && !fu.case_note_id && !['system', 'form'].includes(fu.method);
            return (
              <li key={fu.id} className="flex gap-3">
                <span className={clsx('mt-1 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0', cls)}><Icon size={14} aria-hidden /></span>
                <div className="flex-1 min-w-0 rounded-xl ring-1 ring-inset ring-slate-200/80 bg-white px-3.5 py-2.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <p className="text-[13px] font-semibold text-slate-900">{label}{fu.contact && <span className="font-normal text-slate-600"> · {fu.contact}</span>}</p>
                    <span className="text-[12px] text-slate-500 tabular-nums whitespace-nowrap">{fmtDate(fu.followup_date)}</span>
                  </div>
                  {fu.email_subject && <p className="text-[12.5px] text-slate-600 mt-0.5">Subject: {fu.email_subject}</p>}
                  {fu.note && <p className="text-[13px] text-slate-700 mt-1 whitespace-pre-wrap break-words">{fu.note}</p>}
                  {(fu.outcome || fu.next_followup_date) && (
                    <p className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[12.5px]">
                      {fu.outcome && <span><span className="text-slate-500">Outcome:</span> <span className="font-semibold text-slate-800">{fu.outcome}</span></span>}
                      {fu.next_followup_date && <span className="text-slate-500">Next follow-up {fmtDate(fu.next_followup_date)}</span>}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[12px] text-slate-500">{fu.created_by_name || 'System'} · {fmtDateTime(fu.created_at)}</span>
                    {fu.case_note_id ? (
                      <Pill tone="emerald" label={`Case note${fu.case_note_minutes != null ? ` · ${hoursOf(fu.case_note_minutes)} h` : ''}`} />
                    ) : canMake && (r.coordination_id ? (
                      <Button size="sm" variant="ghost" icon={Clock} onClick={() => setCaseFor(fu)}>Add case note</Button>
                    ) : (
                      <span className="text-[12px] text-slate-500">{r.direction === 'received' ? 'Accept the referral to add a case note' : 'Link the participant’s file to add a case note'}</span>
                    ))}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {caseFor && <CaseNoteModal r={r} fu={caseFor} rounding={rounding} onClose={() => setCaseFor(null)} onDone={() => { setCaseFor(null); onChanged(); }} />}
    </div>
  );
}

// ── Time spent: a duration, a start time, an activity type and billable ───────────────────────
// Shared by "Log follow-up" (optional) and "Add case note" (required). The start defaults to
// "just finished" (now minus the duration) until the person changes it; the activity type starts
// as the one they used last.
function useTimeSpent({ optional, skip = false }) {
  const types = useLoad(`${BASE}/activity-types`, null, { skip });
  const typeList = useMemo(() => (types.data || []).filter((x) => x.claim_type !== 'CANC'), [types.data]);
  const [t, setT] = useState({ minutes: optional ? 0 : null, other: '', start: '', startTouched: false, activity_type_id: '', billable: undefined });
  useEffect(() => {
    if (!typeList.length) return;
    setT((p) => {
      if (p.activity_type_id) return p;
      const last = readLastType();
      return typeList.some((x) => x.id === last) ? { ...p, activity_type_id: last } : p;
    });
  }, [typeList]);
  const mins = t.minutes === 'other' ? Math.round(Number(t.other)) || 0 : num(t.minutes);
  const type = typeList.find((x) => x.id === t.activity_type_id);
  const billable = t.billable === undefined ? (type ? type.billable_default !== false : true) : t.billable;
  const startM = toMinutes(t.start);
  const endM = startM != null && mins > 0 ? startM + mins : null;
  const autoStart = (p, dur) => (p.startTouched || !(dur > 0) ? p.start : hhmm(Math.max(0, nowMinutes() - dur)));
  const pick = (m) => setT((p) => ({ ...p, minutes: m, start: autoStart(p, m === 'other' ? Math.round(Number(p.other)) || 0 : m) }));
  const setOther = (val) => setT((p) => ({ ...p, other: val, start: autoStart(p, Math.round(Number(val)) || 0) }));
  const wanted = t.minutes === 'other' || mins > 0;
  const check = () => {
    const e = {};
    if (!wanted) { if (!optional) e.minutes = 'Choose how long it took.'; return e; }
    if (t.minutes === 'other' && !(mins > 0)) e.minutes = 'Enter the minutes, for example 20.';
    else if (mins > 720) e.minutes = 'Enter 720 minutes (12 hours) or less.';
    if (startM == null) e.start = 'Enter the start time.';
    else if (endM != null && endM >= 24 * 60) e.start = 'The time must finish before midnight. Choose an earlier start.';
    if (!t.activity_type_id) e.activity_type_id = 'Choose an activity type.';
    return e;
  };
  const payload = () => ({ start_time: hhmm(startM), end_time: hhmm(endM), duration_minutes: mins, activity_type_id: t.activity_type_id, billable });
  return { t, setT, types, typeList, mins, billable, endM, pick, setOther, wanted, check, payload };
}

let tsSeq = 0;
function TimeSpentFields({ ts, err, optional, rounding }) {
  const [idBase] = useState(() => { tsSeq += 1; return `ref-ts-${tsSeq}`; });
  const { t, setT, types, typeList, billable, endM, pick, setOther, wanted } = ts;
  const opts = [...(optional ? [{ value: 0, label: 'No time' }] : []), ...DURATIONS.map((d) => ({ value: d, label: `${d} min` })), { value: 'other', label: 'Other' }];
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <p id={`${idBase}-dur`} className="text-[12.5px] font-semibold text-slate-700">
          How long{!optional && <span className="text-rose-500 ml-0.5" aria-hidden>*</span>}
        </p>
        <div role="radiogroup" aria-labelledby={`${idBase}-dur`} className="flex flex-wrap gap-1.5">
          {opts.map((o) => {
            const on = t.minutes === o.value;
            return (
              <button key={o.value} type="button" role="radio" aria-checked={on} onClick={() => pick(o.value)}
                className={clsx('h-11 sm:h-9 px-3.5 rounded-xl text-[13px] font-semibold ring-1 ring-inset transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                  on ? 'bg-violet-600 text-white ring-violet-600' : 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-50')}>
                {o.label}
              </button>
            );
          })}
        </div>
        {err.minutes && t.minutes !== 'other' && <p className="text-[12px] text-rose-600">{err.minutes}</p>}
      </div>
      {wanted && (
        <>
          <div className="grid grid-cols-2 gap-3">
            {t.minutes === 'other' && (
              <Field label="Minutes" required error={err.minutes}>
                <Input type="number" inputMode="numeric" min={1} max={720} step={1} value={t.other} onChange={(e) => setOther(e.target.value)} placeholder="20" className="bg-white" />
              </Field>
            )}
            <Field label="Start time" required error={err.start} hint={!err.start && endM != null && endM < 24 * 60 ? `Finishes at ${clock(endM)}` : undefined}>
              <Input type="time" value={t.start} className="bg-white" onChange={(e) => setT((p) => ({ ...p, start: e.target.value, startTouched: true }))} />
            </Field>
          </div>
          <Field label="Activity type" required error={err.activity_type_id || types.error || undefined}>
            <Select value={t.activity_type_id} onChange={(x) => setT((p) => ({ ...p, activity_type_id: x, billable: undefined }))}
              placeholder={types.loading ? 'Loading…' : 'What was this time for?'} className={clsx('bg-white', READABLE_PLACEHOLDER)}
              options={typeList.map((x) => ({ value: x.id, label: x.name, hint: x.billable_default === false ? 'Not billable by default' : 'Billable by default' }))} />
          </Field>
          <Switch label="Billable" hint={billable ? 'Claimed from the participant’s plan.' : 'Recorded, not claimed.'} checked={billable} onChange={(x) => setT((p) => ({ ...p, billable: x }))} />
          {rounding > 1 && <p className="text-[12px] text-slate-500">Rounded to the nearest {rounding} minutes for pay and claims.</p>}
        </>
      )}
    </div>
  );
}

// Lets Notes & Billing, SC hours and Overview refresh, the same way "Log time" does.
function announceTime(entry) {
  try { window.dispatchEvent(new CustomEvent('sc:time-saved', { detail: entry || null })); } catch { /* old browsers: lists refresh on next load */ }
}
// One toast for a saved case note, with any time-entry warnings folded in.
function caseNoteToast(lead, res) {
  const flags = res?.data?.flags || [];
  const msg = `${lead} · ${hoursOf(res?.data?.entry?.minutes_rounded)} h in Notes & Billing`;
  if (flags.length) toast(`${msg}. Check: ${flags.map(flagText).join(' · ')}`, { icon: <AlertTriangle size={16} className="text-amber-600" aria-hidden />, duration: 7000 });
  else toast.success(msg);
}

let fuSeq = 0;
function LogFollowUpForm({ r, M, canCase, rounding, onCancel, onSaved }) {
  const today = todayIso();
  const [ids] = useState(() => { fuSeq += 1; return { method: `ref-fu-method-${fuSeq}`, time: `ref-fu-time-${fuSeq}` }; });
  const [v, setV] = useState(() => ({
    followup_date: today, method: 'call',
    contact: (r.direction === 'received' ? r.referrer_name : r.contact_person) || '',
    note: '', outcome: '',
    next_followup_date: M.isOpen(r) ? addBusinessDays(today, M.followupDays) : '',
  }));
  const canTime = canCase && !!r.coordination_id;
  const ts = useTimeSpent({ optional: true, skip: !canTime });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { const val = e && e.target ? e.target.value : e; setV((p) => ({ ...p, [k]: val })); setErr((x) => ({ ...x, [k]: undefined })); };
  const withTime = canTime && ts.wanted;
  const save = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!v.note.trim() && !v.outcome.trim()) errs.note = 'Add a note or an outcome.';
    if (!v.followup_date) errs.followup_date = 'Enter the date.';
    const timeErrs = withTime ? ts.check() : {};
    setErr({ ...errs, time: timeErrs });
    if (Object.keys(errs).length || Object.keys(timeErrs).length) return;
    setBusy(true);
    let fu = null;
    try {
      const res = await api.post(`${BASE}/${r.id}/followups`, {
        followup_date: v.followup_date, method: v.method, contact: v.contact.trim() || null, note: v.note.trim() || null,
        outcome: v.outcome.trim() || null, next_followup_date: v.next_followup_date || null,
      });
      fu = res.data?.followup;
    } catch (er) { toast.error(errMsg(er)); setBusy(false); return; }
    // The follow-up is saved. The time goes on as its case note in the same click.
    if (withTime && fu?.id) {
      try {
        const body = ts.payload();
        const res = await api.post(`${BASE}/${r.id}/followups/${fu.id}/case-note`, { note_date: v.followup_date, ...body });
        rememberType(body.activity_type_id);
        announceTime(res.data?.entry);
        caseNoteToast('Follow-up logged', res);
      } catch (er) {
        toast.error(`Follow-up logged, but the time wasn’t saved. ${errMsg(er)} Use Add case note on the follow-up to try again.`, { duration: 9000 });
      }
    } else toast.success('Follow-up logged');
    setBusy(false);
    onSaved();
  };
  return (
    <form onSubmit={save} noValidate className="rounded-2xl ring-1 ring-inset ring-violet-200 bg-violet-50/40 p-4 space-y-3">
      <p className="text-[13.5px] font-semibold text-slate-900">Log a follow-up</p>
      <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3 items-end">
        <Field label="Date" required error={err.followup_date}><Input type="date" value={v.followup_date} max={today} onChange={set('followup_date')} className="bg-white" /></Field>
        <div className="space-y-1.5">
          <p id={ids.method} className="text-[12.5px] font-semibold text-slate-700">How</p>
          <MethodPicker value={v.method} onChange={set('method')} labelId={ids.method} />
        </div>
      </div>
      <Field label="Contact" hint="Who you spoke to or wrote to."><Input value={v.contact} maxLength={200} onChange={set('contact')} className="bg-white" /></Field>
      <Field label="Note" error={err.note}><Textarea rows={3} value={v.note} maxLength={5000} onChange={set('note')} placeholder="What was said, what was agreed." className="bg-white" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Outcome"><Input value={v.outcome} maxLength={200} onChange={set('outcome')} placeholder="For example: left a voicemail" className="bg-white" /></Field>
        <Field label="Next follow-up" hint={r.next_followup_date ? `Leave empty to keep ${fmtDate(r.next_followup_date)}.` : 'Leave empty for no reminder.'}>
          <Input type="date" value={v.next_followup_date} min={today} onChange={set('next_followup_date')} className="bg-white" />
        </Field>
      </div>
      {canTime && (
        <div role="group" aria-labelledby={ids.time} className="rounded-xl bg-white ring-1 ring-inset ring-slate-200/80 p-3.5 space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <p id={ids.time} className="text-[13px] font-semibold text-slate-900 inline-flex items-center gap-1.5"><Clock size={14} className="text-violet-700" aria-hidden />Time spent</p>
            <span className="text-[12px] text-slate-500">Optional. Saved as a case note in Notes & Billing.</span>
          </div>
          <TimeSpentFields ts={ts} err={err.time || {}} optional rounding={rounding} />
        </div>
      )}
      {canCase && !r.coordination_id && (
        <p className="text-[12.5px] text-slate-600 flex items-start gap-1.5">
          <Clock size={14} className="mt-0.5 text-slate-500 flex-shrink-0" aria-hidden />
          {r.direction === 'received' ? 'Time spent can be recorded once the referral is accepted and the participant is set up.' : 'Time spent can be recorded once the referral is linked to the participant’s file.'}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={busy}>{withTime ? 'Save follow-up and time' : 'Save follow-up'}</Button>
      </div>
    </form>
  );
}

function MethodPicker({ value, onChange, labelId }) {
  return (
    <div role="radiogroup" aria-labelledby={labelId} className="inline-flex bg-white ring-1 ring-inset ring-slate-200 p-0.5 rounded-xl">
      {METHODS.map((m) => (
        <button key={m.value} type="button" role="radio" aria-checked={value === m.value} onClick={() => onChange(m.value)}
          className={clsx('inline-flex items-center gap-1.5 rounded-[10px] px-3 h-10 sm:h-9 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
            value === m.value ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-50')}>
          <m.icon size={13} aria-hidden />{m.label}
        </button>
      ))}
    </div>
  );
}

// "Add case note" on an earlier follow-up (5.1, 6.8): a time entry in Notes & Billing with a
// duration and an activity type.
function CaseNoteModal({ r, fu, rounding, onClose, onDone }) {
  const [date, setDate] = useState(iso10(fu.followup_date) || todayIso());
  const [summary, setSummary] = useState('');
  const ts = useTimeSpent({ optional: false });
  const [err, setErr] = useState({});
  const confirm = async () => {
    const e = ts.check();
    if (!date) e.date = 'Enter the date.';
    setErr(e);
    if (Object.keys(e).length) return false;
    const body = ts.payload();
    const res = await api.post(`${BASE}/${r.id}/followups/${fu.id}/case-note`, { note_date: date, ...body, summary: summary.trim() || undefined });
    rememberType(body.activity_type_id);
    announceTime(res.data?.entry);
    caseNoteToast('Case note added', res);
    onDone();
    return true;
  };
  return (
    <ActionModal title="Add case note" sub="Saved to Notes & Billing for this participant, as time worked." confirmLabel="Save case note" onClose={onClose} onConfirm={confirm} width="md">
      <Field label="Date" required error={err.date}><Input type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} /></Field>
      <TimeSpentFields ts={ts} err={err} rounding={rounding} />
      <Field label="Note" hint="Leave empty to use the follow-up’s note and outcome.">
        <Textarea rows={3} value={summary} maxLength={10000} onChange={(e) => setSummary(e.target.value)} placeholder={fu.note || ''} />
      </Field>
    </ActionModal>
  );
}

// ── Online forms (6.7) ────────────────────────────────────────────────────────────────────────
function OnlineForms({ r, M, forms, canManage, onChanged }) {
  const [resend, setResend] = useState(null);
  const [cancel, setCancel] = useState(null);
  const [busy, setBusy] = useState('');
  const remind = async (f) => {
    setBusy(f.id);
    try { const res = await api.post(`${BASE}/${r.id}/forms/${f.id}/remind`); emailToast(res.data?.email || { sent: true, message: 'Reminder sent' }); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };
  const copyLink = async (f) => {
    if (await copyToClipboard(f.link)) toast.success('Form link copied');
    else toast.error('Could not copy the link. Use Resend to email it again.');
  };
  if (!forms.length) {
    return (
      <p className="text-[13px] text-slate-500 rounded-xl bg-slate-50 px-4 py-3">
        No form sent yet. {r.direction === 'received'
          ? 'The Referral Details form asks the referrer for anything missing; their answers save straight to this referral.'
          : 'Sending the referral emails the provider a Referral Response form, so their decision lands here.'}
      </p>
    );
  }
  return (
    <ul className="space-y-2.5">
      {forms.map((f) => {
        const live = f.state === 'sent' || f.state === 'opened';
        return (
          <li key={f.id} className={clsx('rounded-xl ring-1 ring-inset px-4 py-3', f.state === 'submitted' ? 'ring-violet-200 bg-violet-50/30' : 'ring-slate-200/80 bg-white', f.state === 'cancelled' && 'opacity-70')}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-slate-900 flex flex-wrap items-center gap-2">
                  {f.form_name || M.formNames[f.form_type]} form
                  <Pill tone={FORM_TONE[f.state]} label={f.state_label || M.formStates[f.state]} />
                </p>
                <p className="text-[12.5px] text-slate-600 mt-0.5 break-all">To {f.sent_to_name ? `${f.sent_to_name} · ` : ''}{f.sent_to_email}</p>
              </div>
              {canManage && !FINAL.includes(r.status) && (live || f.state === 'expired') && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {live && <Button size="sm" variant="secondary" icon={RefreshCw} loading={busy === f.id} onClick={() => remind(f)}>Send reminder</Button>}
                  <RowMenu label="More form actions" items={[
                    live && f.link && { label: 'Copy link', icon: ClipboardCopy, onClick: () => copyLink(f) },
                    { label: 'Resend with a new link', icon: Send, onClick: () => setResend(f) },
                    live && { label: 'Cancel link', icon: Ban, danger: true, onClick: () => setCancel(f) },
                  ]} />
                </div>
              )}
            </div>
            <p className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] text-slate-500">
              <span>Sent {fmtDateTime(f.sent_at)}</span>
              {f.opened_at && <span>Opened {fmtDateTime(f.opened_at)}</span>}
              {f.state === 'submitted' && <span className="font-semibold text-violet-700">Submitted {fmtDateTime(f.submitted_at)}{f.submitted_by_name ? ` by ${f.submitted_by_name}` : ''}</span>}
              {live && <span>Expires {fmtDate(f.expires_on)}</span>}
              {f.state === 'expired' && <span className="text-amber-700 font-semibold">Expired {fmtDate(f.expires_on)}</span>}
              {num(f.reminders_sent) > 0 && <span>{num(f.reminders_sent)} {num(f.reminders_sent) === 1 ? 'reminder' : 'reminders'}</span>}
            </p>
            {f.state === 'submitted' && f.answers && (
              <details className="mt-2 group">
                <summary className="cursor-pointer text-[12.5px] font-semibold text-indigo-700 hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 w-fit">View answers</summary>
                <div className="mt-3 rounded-xl bg-white ring-1 ring-inset ring-slate-200/80 p-3">
                  <p className="text-[12px] text-slate-500 mb-2">Read only. Saved {fmtDateTime(f.submitted_at)}.</p>
                  <AnswerList answers={parseJson(f.answers)} formType={f.form_type} />
                </div>
              </details>
            )}
          </li>
        );
      })}
      {resend && <ResendFormModal r={r} form={resend} onClose={() => setResend(null)} onDone={() => { setResend(null); onChanged(); }} />}
      {cancel && (
        <ConfirmModal open title="Cancel this form link?" confirmLabel="Cancel link" danger onClose={() => setCancel(null)}
          onConfirm={async () => { await api.post(`${BASE}/${r.id}/forms/${cancel.id}/cancel`); toast.success('Form link cancelled'); onChanged(); }}>
          The link sent to {cancel.sent_to_email} stops working straight away. You can send a new link at any time.
        </ConfirmModal>
      )}
    </ul>
  );
}

// ── Attachments ───────────────────────────────────────────────────────────────────────────────
function Attachments({ r, attachments, canManage, onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [downloading, setDownloading] = useState('');
  const canUpload = canManage && !FINAL.includes(r.status);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_FILE) { toast.error('Files can be 15 MB at most.'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try { await api.post(`${BASE}/${r.id}/attachments`, fd); toast.success(`${file.name} added`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  const download = async (a) => {
    setDownloading(a.id);
    try { await downloadFile(`${BASE}/${r.id}/attachments/${a.id}/download`, { filename: a.name }); }
    catch (e) { toast.error(errMsg(e, 'Could not download the file.')); } finally { setDownloading(''); }
  };
  return (
    <div className="space-y-2.5">
      {canUpload && (
        <div className="flex flex-wrap items-center gap-2">
          <input ref={inputRef} type="file" hidden accept={ACCEPT_FILES} onChange={(e) => upload(e.target.files && e.target.files[0])} />
          <Button size="sm" variant="secondary" icon={Upload} loading={uploading} onClick={() => inputRef.current && inputRef.current.click()}>Upload file</Button>
          <span className="text-[12px] text-slate-500">PDF, Word, Excel, CSV, text or images. 15 MB at most.</span>
        </div>
      )}
      {!attachments.length ? (
        <p className="text-[13px] text-slate-500 rounded-xl bg-slate-50 px-4 py-3">No files yet. Files the other party attaches to an online form show here too.</p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-inset ring-slate-200/80 bg-white">
          {attachments.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-3 px-3.5 py-2.5">
              <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0"><Paperclip size={14} aria-hidden /></span>
              <div className="flex-1 min-w-[160px]">
                <p className="text-[13px] font-semibold text-slate-900 break-all">{a.name}</p>
                <p className="text-[12px] text-slate-500">
                  {fileSize(a.file_size)} · {a.uploaded_by_name || 'Unknown'} · {fmtDateTime(a.created_at)}
                  {a.form_id && <span className="ml-1.5 text-violet-700 font-semibold">From online form</span>}
                </p>
              </div>
              {canManage && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" icon={Download} loading={downloading === a.id} onClick={() => download(a)} aria-label={`Download ${a.name}`}>Download</Button>
                  {canUpload && <Button size="sm" variant="ghost" icon={Trash2} onClick={() => setRemoving(a)} aria-label={`Remove ${a.name}`} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">Remove</Button>}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {!canManage && attachments.length > 0 && <p className="text-[12px] text-slate-500">You need the Manage referrals permission to download files.</p>}
      {removing && (
        <ConfirmModal open title={`Remove ${removing.name}?`} confirmLabel="Remove file" danger onClose={() => setRemoving(null)}
          onConfirm={async () => { await api.delete(`${BASE}/${r.id}/attachments/${removing.id}`); toast.success('File removed'); onChanged(); }}>
          It no longer shows on the referral. The removal is kept in the history.
        </ConfirmModal>
      )}
    </div>
  );
}

// ── Audit trail (collapsed) ───────────────────────────────────────────────────────────────────
function AuditTrail({ audit, M }) {
  if (!audit.length) return <p className="text-[13px] text-slate-500">No history yet.</p>;
  const summary = (a) => {
    const o = parseJson(a.old_value) || {}; const n = parseJson(a.new_value) || {}; const det = parseJson(a.detail) || {};
    const bits = [];
    if (n.status && o.status !== n.status) bits.push(`${M.labels[o.status] || o.status || '—'} → ${M.labels[n.status] || n.status}`);
    if (Object.prototype.hasOwnProperty.call(n, 'next_followup_date') && a.action !== 'status_change') {
      bits.push(`Next follow-up ${o.next_followup_date ? fmtDate(o.next_followup_date) : 'not set'} → ${n.next_followup_date ? fmtDate(n.next_followup_date) : 'not set'}`);
    }
    if (a.action === 'update') {
      const keys = Object.keys(n).filter((k) => !['updated_at', 'next_followup_date'].includes(k));
      if (keys.length) bits.push(`Changed ${keys.map((k) => words(k).toLowerCase()).join(', ')}`);
    }
    if (det.to) bits.push(`To ${det.to}`);
    if (det.outcome) bits.push(det.outcome);
    if (det.name) bits.push(det.name);
    if (det.reason) bits.push(det.reason);
    return bits.join(' · ');
  };
  return (
    <details className="group rounded-xl ring-1 ring-inset ring-slate-200/80 bg-white">
      <summary className="cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-2 px-4 py-3 text-[13px] font-semibold text-slate-700 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
        <span>Show the full history ({audit.length} {audit.length === 1 ? 'event' : 'events'})</span>
        <ChevronDown size={15} className="text-slate-500 transition-transform group-open:rotate-180" aria-hidden />
      </summary>
      <ol className="border-t border-slate-100 divide-y divide-slate-100">
        {audit.map((a) => {
          const s = summary(a);
          return (
            <li key={a.id} className="px-4 py-2.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
              <div className="min-w-0">
                <p className="text-[13px] text-slate-800"><span className="font-semibold">{AUDIT_LABELS[a.action] || words(a.action)}</span>{a.actor_name && <span className="text-slate-500"> · {a.actor_name}</span>}</p>
                {s && <p className="text-[12px] text-slate-500 break-words">{s}</p>}
              </div>
              <span className="text-[12px] text-slate-500 tabular-nums whitespace-nowrap">{fmtDateTime(a.created_at)}</span>
            </li>
          );
        })}
      </ol>
    </details>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Action modals
// ═══════════════════════════════════════════════════════════════════════════════════════════════
let actSeq = 0;
// A short decision in a Modal. onConfirm returns false to stay open; a thrown error is toasted and
// keeps the modal open, so nothing typed is lost.
function ActionModal({ title, sub, confirmLabel, danger, variant = 'primary', disabled, onConfirm, onClose, children, width = 'sm' }) {
  const [formId] = useState(() => { actSeq += 1; return `ref-act-${actSeq}`; });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (disabled || busy) return;
    setBusy(true);
    let ok = false;
    try { ok = (await onConfirm()) !== false; } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Modal open onClose={onClose} title={title} sub={sub} width={width}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" form={formId} variant={danger ? 'danger' : variant} loading={busy} disabled={disabled}>{confirmLabel}</Button></>}>
      <form id={formId} onSubmit={submit} noValidate className="space-y-4">{children}</form>
    </Modal>
  );
}

function OutcomeSwitch({ r, value, onChange }) {
  if (r.direction !== 'received') return null;
  return (
    <Switch label="Email the outcome to the referrer" checked={value && !!r.referrer_email} onChange={onChange} disabled={!r.referrer_email}
      hint={r.referrer_email ? `Sent to ${r.referrer_email} from your outcome template.` : 'There’s no referrer email on this referral.'} />
  );
}

function AcceptModal({ r, assignees, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [coord, setCoord] = useState(r.assigned_user_id || '');
  const [send, setSend] = useState(!!r.referrer_email);
  const [linkExisting, setLinkExisting] = useState(false);
  const [client, setClient] = useState(null);
  const missingName = !String(r.participant_first_name || '').trim() || !String(r.participant_last_name || '').trim();
  const confirm = async () => {
    const res = await api.post(`${BASE}/${r.id}/accept`, {
      outcome_note: note.trim() || undefined, coordinator_id: coord || undefined, client_id: (linkExisting && client?.id) || undefined, send_outcome: send && !!r.referrer_email,
    });
    toast.success('Referral accepted');
    emailToast(res.data?.email);
    onDone(res.data);
    return false;
  };
  return (
    <ActionModal title="Accept referral" confirmLabel="Accept and set up participant" width="md" disabled={missingName || (linkExisting && !client)} onClose={onClose} onConfirm={confirm}
      sub={`${fullName(r.participant_first_name, r.participant_last_name) || 'The participant'} is added as a support coordination participant, ready for the setup checklist.`}>
      {missingName && <Banner tone="red" title="The participant needs a first and last name">Edit the referral to add them, then accept it.</Banner>}
      <Banner tone="violet" icon={Info}>
        {r.ndis_number ? `Rostery looks for an existing participant with NDIS number ${r.ndis_number} first, so nobody is created twice.` : 'There’s no NDIS number on this referral, so a new participant is added unless you link an existing one.'}
      </Banner>
      <Field label="Support coordinator" hint="Defaults to the person the referral is assigned to.">
        <Select value={coord} onChange={setCoord} allowEmpty emptyLabel="Not assigned yet" options={assignees.map((a) => ({ value: a.id, label: a.name, hint: a.email }))} />
      </Field>
      <Checkbox label="Link to a participant who is already in Rostery" checked={linkExisting} onChange={setLinkExisting} />
      {linkExisting && <Field label="Existing participant" required><ClientPicker value={client} onChange={setClient} /></Field>}
      <Field label="Outcome note"><Textarea rows={2} value={note} maxLength={5000} onChange={(e) => setNote(e.target.value)} placeholder="Optional. Included in the outcome email." /></Field>
      <OutcomeSwitch r={r} value={send} onChange={setSend} />
    </ActionModal>
  );
}

function AcceptedModal({ result, onClose }) {
  const cl = result.client || {};
  const eng = result.engagement || {};
  const name = fullName(cl.first_name, cl.last_name) || 'the participant';
  return (
    <Modal open onClose={onClose} width="lg" title="Referral accepted" sub="Finish the setup checklist so this participant’s time can be claimed."
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Stay on the referral</Button>
          {eng.id && <Link to={`/support-coordination/${eng.id}`} onClick={onClose} className={clsx(LINK_BTN, 'text-[13px] h-9 px-3.5 bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm')}>Open participant file<ExternalLink size={14} aria-hidden /></Link>}
        </>
      )}>
      <div className="space-y-4">
        <Banner tone="emerald">
          {cl.existing ? `Linked to the existing participant ${name}${cl.ndis_number ? ` (NDIS ${cl.ndis_number})` : ''}, so no duplicate was created.` : `Added ${name} as a participant.`}
          {' '}{eng.existing ? 'Linked to their current support coordination file.' : 'Opened a support coordination file for them.'}
        </Banner>
        {result.checklist && <SetupChecklist checklist={result.checklist} />}
      </div>
    </Modal>
  );
}

function DeclineModal({ r, onClose, onDone }) {
  const [send, setSend] = useState(!!r.referrer_email);
  return (
    <ReasonModal open onClose={onClose} title="Decline referral" sub="A reason is required and is kept on the referral." label="Reason for declining" confirmLabel="Decline referral" danger
      extra={<div className="mb-4"><OutcomeSwitch r={r} value={send} onChange={setSend} /></div>}
      onConfirm={async (reason) => {
        try {
          const res = await api.post(`${BASE}/${r.id}/decline`, { reason, send_outcome: send && !!r.referrer_email });
          toast.success('Referral declined'); emailToast(res.data?.email); onDone();
        } catch (e) { toast.error(errMsg(e)); }
      }} />
  );
}

function WithdrawModal({ r, onClose, onDone }) {
  return (
    <ReasonModal open onClose={onClose} title="Withdraw referral" sub="Any open form link is cancelled, so the provider can no longer respond." label="Reason for withdrawing" confirmLabel="Withdraw referral" danger
      onConfirm={async (reason) => {
        try { await api.post(`${BASE}/${r.id}/withdraw`, { reason }); toast.success('Referral withdrawn'); onDone(); } catch (e) { toast.error(errMsg(e)); }
      }} />
  );
}

function WaitlistModal({ r, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [send, setSend] = useState(!!r.referrer_email);
  return (
    <ActionModal title="Waitlist referral" sub="The referral stays open, with follow-up reminders, until you accept, decline or close it." confirmLabel="Waitlist referral" onClose={onClose}
      onConfirm={async () => {
        const res = await api.post(`${BASE}/${r.id}/waitlist`, { note: note.trim() || undefined, send_outcome: send && !!r.referrer_email });
        toast.success('Referral waitlisted'); emailToast(res.data?.email); onDone(); return true;
      }}>
      <Field label="Note"><Textarea rows={3} value={note} maxLength={5000} onChange={(e) => setNote(e.target.value)} placeholder="Optional. For example, the expected wait." /></Field>
      <OutcomeSwitch r={r} value={send} onChange={setSend} />
    </ActionModal>
  );
}

function CloseModal({ r, M, onClose, onDone }) {
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [send, setSend] = useState(false);
  return (
    <ActionModal title="Close referral" sub="Closing needs an outcome. A closed referral can’t be changed, and any open form link is cancelled." confirmLabel="Close referral" disabled={!outcome} onClose={onClose}
      onConfirm={async () => {
        const res = await api.post(`${BASE}/${r.id}/close`, { outcome, outcome_note: note.trim() || undefined, send_outcome: send && !!r.referrer_email });
        toast.success('Referral closed'); emailToast(res.data?.email); onDone(); return true;
      }}>
      <Field label="Outcome" required><Select value={outcome} onChange={setOutcome} placeholder="Choose the outcome" className={READABLE_PLACEHOLDER} options={M.closeOutcomes.map((o) => ({ value: o, label: o }))} /></Field>
      <Field label="Outcome note"><Textarea rows={3} value={note} maxLength={5000} onChange={(e) => setNote(e.target.value)} /></Field>
      <OutcomeSwitch r={r} value={send} onChange={setSend} />
    </ActionModal>
  );
}

function SendFormModal({ r, M, mode, onClose, onDone }) {
  const received = r.direction === 'received';
  const [to, setTo] = useState((received ? r.referrer_email : r.contact_email) || '');
  const [name, setName] = useState((received ? r.referrer_name : r.contact_person) || '');
  const formName = M.formNames[received ? 'referral_details' : 'referral_response'];
  const bad = to.trim() && !isEmail(to);
  const expiry = M.expiryDays ? ` The link expires after ${M.expiryDays} days.` : '';
  return (
    <ActionModal title={mode === 'request_info' ? 'Request more information' : `Send ${formName} form`} confirmLabel="Send form" disabled={!to.trim() || bad} onClose={onClose}
      sub={mode === 'request_info'
        ? `Emails the ${formName} form to the referrer and marks the referral Information requested.${expiry}`
        : `Emails a secure ${formName} form link. No login is needed.${expiry} An older open link for this form is cancelled.`}
      onConfirm={async () => {
        const res = await api.post(mode === 'request_info' ? `${BASE}/${r.id}/request-info` : `${BASE}/${r.id}/forms`, { to_email: to.trim(), to_name: name.trim() || undefined });
        emailToast(res.data?.email || { sent: true, message: 'Form sent' }); onDone(); return true;
      }}>
      <Field label="Email address" required error={bad ? 'This isn’t a valid email address.' : undefined}><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} autoComplete="off" /></Field>
      <Field label="Name"><Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} /></Field>
    </ActionModal>
  );
}

function ResendFormModal({ r, form, onClose, onDone }) {
  const [to, setTo] = useState(form.sent_to_email || '');
  const [name, setName] = useState(form.sent_to_name || '');
  const bad = to.trim() && !isEmail(to);
  return (
    <ActionModal title="Resend with a new link" sub="The old link stops working and a new one is emailed." confirmLabel="Resend form" disabled={!to.trim() || bad} onClose={onClose}
      onConfirm={async () => {
        const res = await api.post(`${BASE}/${r.id}/forms/${form.id}/resend`, { to_email: to.trim(), to_name: name.trim() || undefined });
        emailToast(res.data?.email || { sent: true, message: 'Form resent' }); onDone(); return true;
      }}>
      <Field label="Email address" required error={bad ? 'This isn’t a valid email address.' : undefined}><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      <Field label="Name"><Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} /></Field>
    </ActionModal>
  );
}

function SendReferralModal({ r, M, attachments, onClose, onDone, onEdit }) {
  const [to, setTo] = useState(r.contact_email || '');
  const [name, setName] = useState(r.contact_person || '');
  const [picked, setPicked] = useState([]);
  const consent = r.consent_to_share === true;
  const bad = to.trim() && !isEmail(to);
  const total = attachments.filter((a) => picked.includes(a.id)).reduce((t, a) => t + num(a.file_size), 0);
  const tooBig = total > 20 * 1024 * 1024;
  const toggle = (idv, on) => setPicked((p) => (on ? [...p, idv].slice(0, 10) : p.filter((x) => x !== idv)));
  return (
    <ActionModal title="Send referral" width="md" confirmLabel="Send referral" disabled={!consent || !to.trim() || bad || tooBig} onClose={onClose}
      sub={`Emails the referral to ${r.receiving_org || 'the provider'} with a secure Referral Response form.${M.expiryDays ? ` The link expires after ${M.expiryDays} days.` : ''}`}
      onConfirm={async () => {
        const res = await api.post(`${BASE}/${r.id}/send`, { to_email: to.trim(), to_name: name.trim() || undefined, attachment_ids: picked.length ? picked : undefined });
        toast.success('Referral sent'); emailToast(res.data?.email); onDone(); return true;
      }}>
      {!consent && (
        <Banner tone="red" title="Consent not confirmed" action={<Button size="sm" variant="secondary" onClick={onEdit}>Record consent</Button>}>
          A referral can’t be sent until the participant’s consent to share their information is recorded.
        </Banner>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Email address" required error={bad ? 'This isn’t a valid email address.' : undefined}><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Field label="Contact person"><Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} /></Field>
      </div>
      {attachments.length > 0 && (
        <fieldset className="space-y-2">
          <legend className="text-[12.5px] font-semibold text-slate-700 mb-1.5">Attach files</legend>
          {attachments.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3">
              <Checkbox label={a.name} checked={picked.includes(a.id)} onChange={(on) => toggle(a.id, on)} disabled={!picked.includes(a.id) && picked.length >= 10} />
              <span className="text-[12px] text-slate-500 tabular-nums">{fileSize(a.file_size)}</span>
            </div>
          ))}
          <p className={clsx('text-[12px]', tooBig ? 'text-rose-600 font-semibold' : 'text-slate-500')}>{tooBig ? `The files come to ${fileSize(total)}. Choose files under 20 MB in total.` : 'Up to 10 files, 20 MB in total.'}</p>
        </fieldset>
      )}
    </ActionModal>
  );
}

function MarkSentModal({ r, onClose, onDone }) {
  const today = todayIso();
  const [date, setDate] = useState(today);
  const [note, setNote] = useState('');
  const consent = r.consent_to_share === true;
  return (
    <ActionModal title="Mark as sent" sub="For a referral sent another way, such as the provider’s own online form. The follow-up reminder starts from today." confirmLabel="Mark as sent"
      disabled={!consent || !date || date > today} onClose={onClose}
      onConfirm={async () => {
        await api.post(`${BASE}/${r.id}/mark-sent`, { sent_date: date, note: note.trim() || undefined });
        toast.success('Referral marked as sent'); onDone(); return true;
      }}>
      {!consent && <Banner tone="red" title="Consent not confirmed">Record the participant’s consent on the referral before marking it as sent.</Banner>}
      <Field label="Date sent" required><Input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} /></Field>
      <Field label="How it was sent"><Textarea rows={2} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} placeholder="For example: submitted on the provider’s website" /></Field>
    </ActionModal>
  );
}

function RemoveReferralModal({ r, onClose, onDone }) {
  return (
    <ConfirmModal open title={`Remove ${r.referral_number}?`} confirmLabel="Remove referral" danger onClose={onClose}
      onConfirm={async () => { await api.delete(`${BASE}/${r.id}`); toast.success('Referral removed'); onDone(); }}>
      Only for a referral created by mistake. To finish a real referral, close it with an outcome so the record is kept.
      It disappears from the lists; the removal stays in the history.
    </ConfirmModal>
  );
}

function CopyTextModal({ text, onClose }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.select(); }, []);
  return (
    <Modal open onClose={onClose} title="Referral details" sub="Your browser blocked copying. Select the text and copy it." width="lg"
      footer={<Button variant="secondary" onClick={onClose}>Done</Button>}>
      <Field label="Details to paste"><Textarea ref={ref} rows={14} readOnly value={text} className="font-mono text-[12.5px]" /></Field>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Pickers
// ═══════════════════════════════════════════════════════════════════════════════════════════════
let cbSeq = 0;
// An accessible combobox (input + listbox). Escape closes the list without closing the Sheet.
function Combobox({ id, value, onInput, options, onPick, placeholder, loading, emptyText = 'No matches', disabled, 'aria-invalid': invalid, showWhenEmpty = true }) {
  const [listId] = useState(() => { cbSeq += 1; return `ref-cb-${cbSeq}`; });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  useEffect(() => { setActive(options.length ? 0 : -1); }, [options]);
  const visible = open && (options.length > 0 || (showWhenEmpty && !loading && String(value || '').trim().length > 0));
  // Radix closes the Sheet on Escape in the capture phase at document level, so a window-level
  // capture listener gets there first while the list is showing.
  useEffect(() => {
    if (!visible) return undefined;
    const h = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [visible]);
  const pick = (o) => { onPick(o); setOpen(false); };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(options.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' && visible && active >= 0 && options[active]) { e.preventDefault(); pick(options[active]); }
  };
  return (
    <div className="relative">
      <input id={id} type="text" role="combobox" aria-expanded={visible} aria-controls={listId} aria-autocomplete="list"
        aria-activedescendant={visible && active >= 0 && options[active] ? `${listId}-${active}` : undefined} aria-invalid={invalid}
        value={value} disabled={disabled} placeholder={placeholder} autoComplete="off"
        onChange={(e) => { onInput(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onKeyDown={onKeyDown}
        className={clsx(CONTROL, 'h-10 pr-9')} />
      {loading && <Loader2 size={15} className="absolute right-3 top-[13px] animate-spin text-slate-400" aria-hidden />}
      <ul id={listId} role="listbox" hidden={!visible}
        className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-auto bg-white rounded-xl shadow-card-deep ring-1 ring-slate-200/80 p-1">
        {options.map((o, i) => (
          <li key={o.value} id={`${listId}-${i}`} role="option" aria-selected={i === active}
            onMouseDown={(e) => { e.preventDefault(); pick(o); }} onMouseEnter={() => setActive(i)}
            className={clsx('px-3 py-2 rounded-lg text-[13px] cursor-pointer', i === active ? 'bg-indigo-50 text-indigo-900' : 'text-slate-700')}>
            <span className="block font-medium">{o.label}</span>
            {o.hint && <span className="block text-[12px] text-slate-500">{o.hint}</span>}
          </li>
        ))}
        {!options.length && <li role="option" aria-selected={false} aria-disabled="true" className="px-3 py-2 text-[13px] text-slate-500">{emptyText}</li>}
      </ul>
    </div>
  );
}

// Clients a referral can be made for. Plan Management participants are finance-only and left out.
function ClientPicker({ id, value, onChange, 'aria-invalid': invalid }) {
  const [text, setText] = useState(value?.name || '');
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const chosenName = value ? value.name : null;
  useEffect(() => {
    const term = text.trim();
    if (chosenName != null && term === chosenName) { setOpts([]); setLoading(false); return undefined; }
    if (term.length < 2) { setOpts([]); setLoading(false); return undefined; }
    const my = ++seq.current;
    setLoading(true);
    const t = setTimeout(() => {
      api.get('/clients', { params: { search: term, limit: 20 } }).then((res) => {
        if (my !== seq.current) return;
        const all = Array.isArray(res.data) ? res.data : (res.data?.clients || []);
        setOpts(all.filter((c) => c.service_type !== 'plan_managed').map((c) => ({
          value: c.id, label: fullName(c.first_name, c.last_name) || 'Unnamed participant',
          hint: [c.ndis_number && `NDIS ${c.ndis_number}`, c.service_type && words(c.service_type)].filter(Boolean).join(' · ') || undefined,
        })));
      }).catch(() => { if (my === seq.current) setOpts([]); }).finally(() => { if (my === seq.current) setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [text, chosenName]);
  return (
    <Combobox id={id} aria-invalid={invalid} value={text} loading={loading} options={opts} placeholder="Type a name or NDIS number" showWhenEmpty={!value}
      emptyText={text.trim().length < 2 ? 'Type at least 2 letters' : 'No matching participants'}
      onInput={(t) => { setText(t); if (value) onChange(null); }}
      onPick={(o) => { setText(o.label); onChange({ id: o.value, name: o.label }); }} />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Create and edit
// ═══════════════════════════════════════════════════════════════════════════════════════════════
const RECEIVED_EDIT_KEYS = ['referral_date', 'received_via', 'referrer_name', 'referrer_org', 'referrer_role', 'referrer_phone', 'referrer_email',
  'participant_first_name', 'participant_last_name', 'ndis_number', 'date_of_birth', 'participant_phone', 'participant_email', 'participant_address',
  'plan_start', 'plan_end', 'plan_management_type', 'services_requested', 'support_needs', 'known_risks', 'consent_to_share', 'consent_date'];
const RECEIVED_CREATE_KEYS = [...RECEIVED_EDIT_KEYS, 'assigned_user_id', 'next_followup_date'];
const SENT_EDIT_KEYS = ['referral_date', 'contact_id', 'receiving_org', 'contact_person', 'contact_email', 'contact_phone', 'services_requested', 'reason',
  'support_needs', 'known_risks', 'consent_to_share', 'consent_date'];
const SENT_CREATE_KEYS = [...SENT_EDIT_KEYS, 'client_id', 'coordination_id', 'assigned_user_id', 'next_followup_date'];
const DATE_KEYS = ['referral_date', 'date_of_birth', 'plan_start', 'plan_end', 'consent_date', 'next_followup_date'];

const blankOf = (keys) => Object.fromEntries(keys.map((k) => [k, '']));
const fromReferral = (r, keys) => Object.fromEntries(keys.map((k) => {
  if (k === 'consent_to_share') return [k, r[k] === true ? 'yes' : r[k] === false ? 'no' : ''];
  if (r[k] == null) return [k, ''];
  return [k, DATE_KEYS.includes(k) ? iso10(r[k]) : String(r[k])];
}));
function toBody(v, keys, { omitEmpty = false } = {}) {
  const out = {};
  keys.forEach((k) => {
    let x = v[k];
    if (k === 'consent_to_share') x = x === 'yes' ? true : x === 'no' ? false : null;
    else if (typeof x === 'string') x = x.trim() || null;
    else if (x === undefined) x = null;
    if (omitEmpty && x === null) return;
    out[k] = x;
  });
  return out;
}
function validateReceived(v) {
  const e = {};
  if (!v.referral_date) e.referral_date = 'Enter the date the referral was received.';
  if (!String(v.participant_first_name).trim() && !String(v.participant_last_name).trim()) e.participant_first_name = 'Enter the participant’s name.';
  if (v.referrer_email && !isEmail(v.referrer_email)) e.referrer_email = 'This isn’t a valid email address.';
  if (v.participant_email && !isEmail(v.participant_email)) e.participant_email = 'This isn’t a valid email address.';
  if (v.referrer_phone && !isPhone(v.referrer_phone)) e.referrer_phone = 'This isn’t a valid phone number.';
  if (v.participant_phone && !isPhone(v.participant_phone)) e.participant_phone = 'This isn’t a valid phone number.';
  if (v.ndis_number && !isNdis(v.ndis_number)) e.ndis_number = 'NDIS numbers are 9 digits.';
  if (v.plan_start && v.plan_end && v.plan_end < v.plan_start) e.plan_end = 'The plan end date must be after the start date.';
  return e;
}
function validateSent(v, { needClient }) {
  const e = {};
  if (needClient && !v.client_id) e.client_id = 'Choose the participant this referral is for.';
  if (!String(v.receiving_org).trim()) e.receiving_org = 'Choose a referral contact or enter the organisation.';
  if (!String(v.services_requested).trim()) e.services_requested = 'Enter the service requested.';
  if (v.contact_email && !isEmail(v.contact_email)) e.contact_email = 'This isn’t a valid email address.';
  if (v.contact_phone && !isPhone(v.contact_phone)) e.contact_phone = 'This isn’t a valid phone number.';
  return e;
}
function useFormState(initial) {
  const [v, setV] = useState(initial);
  const [err, setErr] = useState({});
  const set = useCallback((k) => (e) => {
    const val = e && e.target ? e.target.value : e;
    setV((p) => ({ ...p, [k]: val }));
    setErr((x) => (x[k] ? { ...x, [k]: undefined } : x));
  }, []);
  const setMany = useCallback((patch) => {
    setV((p) => ({ ...p, ...patch }));
    setErr((x) => { const n = { ...x }; Object.keys(patch).forEach((k) => { n[k] = undefined; }); return n; });
  }, []);
  return { v, setV, err, setErr, set, setMany };
}

function FormGroup({ title, children }) {
  return (
    <fieldset className="min-w-0">
      <legend className="text-[11px] font-semibold uppercase tracking-[0.08em] text-violet-600 mb-2.5">{title}</legend>
      <div className="space-y-3">{children}</div>
    </fieldset>
  );
}
const Grid2 = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;

function ReceivedFields({ f, assignees, M, create }) {
  const { v, set, err } = f;
  return (
    <div className="space-y-6">
      <FormGroup title="Referral">
        <Grid2>
          <Field label="Date received" required error={err.referral_date}><Input type="date" value={v.referral_date} max={todayIso()} onChange={set('referral_date')} aria-required="true" /></Field>
          <Field label="Received via"><Select value={v.received_via} onChange={set('received_via')} options={VIA_OPTS} allowEmpty emptyLabel="Not recorded" /></Field>
          {create && (
            <Field label="Assigned to" hint="Leave as is to assign it to yourself.">
              <Select value={v.assigned_user_id} onChange={set('assigned_user_id')} allowEmpty emptyLabel="Me" options={assignees.map((a) => ({ value: a.id, label: a.name, hint: a.email }))} />
            </Field>
          )}
          {create && (
            <Field label="First follow-up" hint={`Leave empty for ${M.followupDays} business days after the date received.`}>
              <Input type="date" value={v.next_followup_date} onChange={set('next_followup_date')} />
            </Field>
          )}
        </Grid2>
      </FormGroup>
      <FormGroup title="Referrer">
        <Grid2>
          <Field label="Name"><Input value={v.referrer_name} maxLength={200} onChange={set('referrer_name')} autoComplete="off" /></Field>
          <Field label="Organisation"><Input value={v.referrer_org} maxLength={200} onChange={set('referrer_org')} /></Field>
          <Field label="Role"><Input value={v.referrer_role} maxLength={120} onChange={set('referrer_role')} /></Field>
          <Field label="Phone" error={err.referrer_phone}><Input type="tel" value={v.referrer_phone} onChange={set('referrer_phone')} /></Field>
          <Field label="Email" error={err.referrer_email} className="sm:col-span-2"><Input type="email" value={v.referrer_email} onChange={set('referrer_email')} autoComplete="off" /></Field>
        </Grid2>
      </FormGroup>
      <FormGroup title="Participant">
        <Grid2>
          <Field label="First name" required error={err.participant_first_name}><Input value={v.participant_first_name} maxLength={120} onChange={set('participant_first_name')} aria-required="true" autoComplete="off" /></Field>
          <Field label="Last name" hint="Needed before the referral can be accepted."><Input value={v.participant_last_name} maxLength={120} onChange={set('participant_last_name')} autoComplete="off" /></Field>
          <Field label="NDIS number" error={err.ndis_number}><Input inputMode="numeric" value={v.ndis_number} maxLength={13} onChange={set('ndis_number')} placeholder="9 digits" /></Field>
          <Field label="Date of birth"><Input type="date" value={v.date_of_birth} max={todayIso()} onChange={set('date_of_birth')} /></Field>
          <Field label="Phone" error={err.participant_phone}><Input type="tel" value={v.participant_phone} onChange={set('participant_phone')} /></Field>
          <Field label="Email" error={err.participant_email}><Input type="email" value={v.participant_email} onChange={set('participant_email')} autoComplete="off" /></Field>
          <Field label="Address" className="sm:col-span-2"><Textarea rows={2} value={v.participant_address} maxLength={2000} onChange={set('participant_address')} /></Field>
          <Field label="Plan start"><Input type="date" value={v.plan_start} onChange={set('plan_start')} /></Field>
          <Field label="Plan end" error={err.plan_end}><Input type="date" value={v.plan_end} min={v.plan_start || undefined} onChange={set('plan_end')} /></Field>
          <Field label="Plan management type"><Select value={v.plan_management_type} onChange={set('plan_management_type')} options={PLAN_OPTS} allowEmpty emptyLabel="Not sure" /></Field>
        </Grid2>
      </FormGroup>
      <FormGroup title="Request">
        <Field label="Services requested"><Textarea rows={2} value={v.services_requested} maxLength={5000} onChange={set('services_requested')} /></Field>
        <Field label="Summary of support needs"><Textarea rows={3} value={v.support_needs} maxLength={10000} onChange={set('support_needs')} /></Field>
        <Field label="Known risks"><Textarea rows={2} value={v.known_risks} maxLength={10000} onChange={set('known_risks')} /></Field>
      </FormGroup>
      <ConsentGroup f={f} />
    </div>
  );
}

function ConsentGroup({ f }) {
  const { v, set } = f;
  return (
    <FormGroup title="Consent">
      <Grid2>
        <Field label="Consent to share information"><Select value={v.consent_to_share} onChange={set('consent_to_share')} options={CONSENT_OPTS} allowEmpty emptyLabel="Not recorded" /></Field>
        <Field label="Date consent was given" hint={v.consent_to_share === 'yes' && !v.consent_date ? 'Leave empty for today.' : undefined}>
          <Input type="date" value={v.consent_date} max={todayIso()} onChange={set('consent_date')} />
        </Field>
      </Grid2>
    </FormGroup>
  );
}

function SentFields({ f, assignees, M, create, clientFixedName, contacts }) {
  const { v, set, setMany, err } = f;
  const term = String(v.receiving_org || '').trim().toLowerCase();
  const contactOpts = useMemo(() => (contacts || [])
    .filter((c) => !term || v.contact_id === c.id || [c.organisation, c.contact_person, c.services_offered, c.email].some((x) => String(x || '').toLowerCase().includes(term)))
    .slice(0, 50)
    .map((c) => ({ value: c.id, label: c.organisation, hint: [c.contact_person, c.services_offered].filter(Boolean).join(' · ').slice(0, 140) || undefined, contact: c })), [contacts, term, v.contact_id]);
  const pickContact = (o) => {
    const c = o.contact;
    setMany({ contact_id: c.id, receiving_org: c.organisation || '', contact_person: c.contact_person || '', contact_email: c.email || '', contact_phone: c.phone || '' });
  };
  return (
    <div className="space-y-6">
      <FormGroup title="Participant">
        {clientFixedName != null ? (
          <div className="rounded-xl bg-slate-50 px-3.5 py-2.5">
            <p className="text-[12px] font-medium text-slate-500">Participant</p>
            <p className="text-[13.5px] font-semibold text-slate-900">{clientFixedName || 'This participant'}</p>
          </div>
        ) : (
          <Field label="Participant" required error={err.client_id} hint="Their details are copied onto the referral.">
            <ClientPicker value={v.client_id ? { id: v.client_id, name: v.client_name } : null}
              onChange={(c) => setMany({ client_id: c ? c.id : '', client_name: c ? c.name : '' })} />
          </Field>
        )}
      </FormGroup>
      <FormGroup title="Receiving organisation">
        <Field label="Organisation" required error={err.receiving_org}
          hint={v.contact_id ? 'From your referral contacts.' : term ? 'Not in your referral contacts yet.' : 'Search your referral contacts, or type a new organisation.'}>
          <Combobox value={v.receiving_org} options={contactOpts} onPick={pickContact} placeholder="Search referral contacts"
            emptyText={term ? `“${v.receiving_org}” will be used as a new organisation` : 'No referral contacts yet'}
            onInput={(t) => setMany({ receiving_org: t, contact_id: '' })} />
        </Field>
        <Grid2>
          <Field label="Contact person"><Input value={v.contact_person} maxLength={200} onChange={set('contact_person')} /></Field>
          <Field label="Contact phone" error={err.contact_phone}><Input type="tel" value={v.contact_phone} onChange={set('contact_phone')} /></Field>
          <Field label="Contact email" error={err.contact_email} hint="The referral and its response form are emailed here." className="sm:col-span-2">
            <Input type="email" value={v.contact_email} onChange={set('contact_email')} autoComplete="off" />
          </Field>
        </Grid2>
        {create && !v.contact_id && term && (
          <Checkbox label="Save this organisation to your referral contacts" checked={!!v.save_contact} onChange={(x) => setMany({ save_contact: x })} />
        )}
      </FormGroup>
      <FormGroup title="Request">
        <Field label="Service requested" required error={err.services_requested}><Textarea rows={2} value={v.services_requested} maxLength={5000} onChange={set('services_requested')} aria-required="true" /></Field>
        <Field label="Reason for referral"><Textarea rows={2} value={v.reason} maxLength={5000} onChange={set('reason')} /></Field>
        <Field label="Support needs"><Textarea rows={3} value={v.support_needs} maxLength={10000} onChange={set('support_needs')} /></Field>
        <Field label="Known risks"><Textarea rows={2} value={v.known_risks} maxLength={10000} onChange={set('known_risks')} /></Field>
      </FormGroup>
      <ConsentGroup f={f} />
      {v.consent_to_share !== 'yes' && <Banner tone="amber">You can save the referral as a draft now. It can’t be sent, or its details copied, until consent is recorded.</Banner>}
      <FormGroup title="Referral">
        <Grid2>
          <Field label="Referral date"><Input type="date" value={v.referral_date} max={todayIso()} onChange={set('referral_date')} /></Field>
          {create && (
            <Field label="Assigned to" hint="Leave as is to assign it to yourself.">
              <Select value={v.assigned_user_id} onChange={set('assigned_user_id')} allowEmpty emptyLabel="Me" options={assignees.map((a) => ({ value: a.id, label: a.name, hint: a.email }))} />
            </Field>
          )}
          {create && (
            <Field label="First follow-up" hint={`Leave empty for ${M.followupDays} business days after it is sent.`}>
              <Input type="date" value={v.next_followup_date} onChange={set('next_followup_date')} />
            </Field>
          )}
        </Grid2>
      </FormGroup>
    </div>
  );
}

let sheetFormSeq = 0;
function NewReceivedSheet({ onClose, onCreated, assignees, M }) {
  const [formId] = useState(() => { sheetFormSeq += 1; return `ref-new-rec-${sheetFormSeq}`; });
  const f = useFormState(() => ({ ...blankOf(RECEIVED_CREATE_KEYS), referral_date: todayIso(), received_via: 'email' }));
  const [ack, setAck] = useState(true);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    const errs = validateReceived(f.v);
    f.setErr(errs);
    if (Object.keys(errs).length) { toast.error('Check the highlighted fields.'); return; }
    setBusy(true);
    try {
      const hasEmail = !!String(f.v.referrer_email).trim();
      const res = await api.post(`${BASE}/received`, { ...toBody(f.v, RECEIVED_CREATE_KEYS, { omitEmpty: true }), send_acknowledgement: ack && hasEmail });
      toast.success(`Referral ${res.data?.referral?.referral_number || ''} logged`);
      emailToast(res.data?.email);
      onCreated(res.data?.referral);
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  const hasEmail = !!String(f.v.referrer_email).trim();
  return (
    <Sheet open onClose={onClose} icon={ArrowDownLeft} width="lg" title="Log received referral" sub="Someone has referred a participant to you. Missing details can be requested later with the Referral Details form."
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" form={formId} loading={busy}>Log referral</Button></>}>
      <form id={formId} onSubmit={save} noValidate className="space-y-6">
        <ReceivedFields f={f} assignees={assignees} M={M} create />
        <div className="rounded-xl ring-1 ring-inset ring-slate-200/80 px-4 py-3">
          <Switch label="Email an acknowledgement to the referrer" checked={ack && hasEmail} onChange={setAck} disabled={!hasEmail}
            hint={hasEmail ? `Sent to ${f.v.referrer_email} from your acknowledgement template, with the referral number.` : 'Add the referrer’s email to send one.'} />
        </div>
      </form>
    </Sheet>
  );
}

function NewSentSheet({ onClose, onCreated, assignees, M, preset }) {
  const [formId] = useState(() => { sheetFormSeq += 1; return `ref-new-sent-${sheetFormSeq}`; });
  const presetClient = preset?.clientId || '';
  const presetEng = preset?.coordinationId || '';
  // The participant file may pass only the engagement: read its client from it.
  const eng = useLoad(presetEng && !presetClient ? `/support-coordination/${presetEng}` : null);
  const cli = useLoad(presetClient && !preset?.participantName ? `/clients/${presetClient}` : null);
  const contacts = useLoad(`${BASE}/contacts`);
  const f = useFormState(() => ({ ...blankOf(SENT_CREATE_KEYS), client_name: '', save_contact: true, referral_date: todayIso(), client_id: presetClient, coordination_id: presetEng }));
  const { setMany } = f;
  useEffect(() => {
    const e = eng.data;
    if (e && e.client_id) setMany({ client_id: e.client_id, client_name: fullName(e.client_first, e.client_last) });
  }, [eng.data, setMany]);
  const [busy, setBusy] = useState(false);
  const fixed = !!(presetClient || presetEng);
  const c = cli.data?.client || cli.data;
  const fixedName = fixed ? (preset?.participantName || f.v.client_name || fullName(c?.first_name, c?.last_name)) : null;
  const save = async (e) => {
    e.preventDefault();
    const errs = validateSent(f.v, { needClient: true });
    f.setErr(errs);
    if (Object.keys(errs).length) { toast.error(errs.client_id && fixed ? 'This participant could not be loaded. Close and try again.' : 'Check the highlighted fields.'); return; }
    setBusy(true);
    try {
      const body = { ...toBody(f.v, SENT_CREATE_KEYS, { omitEmpty: true }) };
      if (!f.v.contact_id && f.v.save_contact) body.save_contact = true;
      const res = await api.post(`${BASE}/sent`, body);
      toast.success(`Referral ${res.data?.referral?.referral_number || ''} saved as a draft`);
      if (res.data?.contact) toast.success(`${res.data.contact.organisation} added to your referral contacts`);
      onCreated(res.data?.referral);
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} icon={ArrowUpRight} width="lg" title="Send a referral" sub="Saved as a draft. Then send it by email with a Referral Response form, or mark it as sent another way."
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" form={formId} loading={busy}>Save draft</Button></>}>
      <form id={formId} onSubmit={save} noValidate>
        {contacts.error && <div className="mb-4"><Banner tone="amber">Your referral contacts couldn’t be loaded. You can still type the organisation.</Banner></div>}
        <SentFields f={f} assignees={assignees} M={M} create clientFixedName={fixedName} contacts={contacts.data} />
      </form>
    </Sheet>
  );
}

// Edit inside the referral Sheet. Assignment and the next follow-up are changed in the header.
function EditReferral({ r, M, onCancel, onSaved }) {
  const received = r.direction === 'received';
  const keys = received ? RECEIVED_EDIT_KEYS : SENT_EDIT_KEYS;
  const f = useFormState(() => ({ ...fromReferral(r, keys), client_id: r.client_id || '', client_name: fullName(r.participant_first_name, r.participant_last_name) }));
  const contacts = useLoad(received ? null : `${BASE}/contacts`);
  const [busy, setBusy] = useState(false);
  const draft = r.status === 'draft';
  const save = async (e) => {
    e.preventDefault();
    const errs = received ? validateReceived(f.v) : validateSent(f.v, { needClient: draft });
    f.setErr(errs);
    if (Object.keys(errs).length) { toast.error('Check the highlighted fields.'); return; }
    setBusy(true);
    try {
      const body = toBody(f.v, keys);
      if (!received && draft && f.v.client_id && f.v.client_id !== r.client_id) body.client_id = f.v.client_id;
      await api.put(`${BASE}/${r.id}`, body);
      toast.success('Referral updated');
      onSaved();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} noValidate>
      <div className="mb-5"><Banner tone="violet" icon={PenLine} title={`Editing ${r.referral_number}`}>Change the assignee and the next follow-up date at the top of the referral.</Banner></div>
      {received
        ? <ReceivedFields f={f} M={M} assignees={[]} />
        : <SentFields f={f} M={M} assignees={[]} contacts={contacts.data} clientFixedName={draft ? null : fullName(r.participant_first_name, r.participant_last_name)} />}
      <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 px-6 py-3 bg-white/95 backdrop-blur border-t border-slate-100 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={busy}>Save changes</Button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Referral contacts (6.5)
// ═══════════════════════════════════════════════════════════════════════════════════════════════
function ContactsSheet({ onClose, canManage }) {
  const [showRetired, setShowRetired] = useState(false);
  const [q, setQ] = useState('');
  const l = useLoad(`${BASE}/contacts`, showRetired ? { include_inactive: '1' } : {});
  const [edit, setEdit] = useState(null);
  const term = q.trim().toLowerCase();
  const rows = (l.data || []).filter((c) => !term || [c.organisation, c.contact_person, c.email, c.phone, c.services_offered].some((x) => String(x || '').toLowerCase().includes(term)));
  const setActive = async (c, active) => {
    try {
      if (active) await api.put(`${BASE}/contacts/${c.id}`, { is_active: true });
      else await api.delete(`${BASE}/contacts/${c.id}`);
      toast.success(active ? `${c.organisation} restored` : `${c.organisation} retired`);
      l.reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open onClose={onClose} icon={BookUser} width="lg" title="Referral contacts" sub="The providers you refer participants to. Choose one when you send a referral and its details fill in.">
      <Toolbar actions={canManage && <Button icon={Plus} onClick={() => setEdit({})}>Add contact</Button>}>
        <SearchInput value={q} onChange={setQ} placeholder="Search contacts" className="w-full sm:w-64" />
        <Checkbox label="Show retired" checked={showRetired} onChange={setShowRetired} />
      </Toolbar>
      {l.error ? <ErrorBlock error={l.error} onRetry={l.reload} /> : !l.data ? <LoadingBlock /> : !rows.length ? (
        <Empty icon={BookUser} title={term ? 'No contacts match' : 'No referral contacts yet'}
          action={!term && canManage ? <Button icon={Plus} onClick={() => setEdit({})}>Add contact</Button> : null}>
          {term ? 'Try a different search.' : 'Add the providers you refer to most, or tick “save to referral contacts” when you send a referral.'}
        </Empty>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-2xl ring-1 ring-inset ring-slate-200/80 bg-white">
          {rows.map((c) => (
            <li key={c.id} className={clsx('flex items-start gap-3 px-4 py-3', !c.is_active && 'opacity-60')}>
              <span className="mt-0.5 w-9 h-9 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center flex-shrink-0 text-[12px] font-bold" aria-hidden>
                {String(c.organisation || '?').slice(0, 2).toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold text-slate-900 flex flex-wrap items-center gap-2">{c.organisation}{!c.is_active && <Pill tone="slate" label="Retired" />}</p>
                <p className="text-[12.5px] text-slate-600 flex flex-wrap gap-x-3">
                  {c.contact_person && <span>{c.contact_person}</span>}
                  {c.email && <a href={`mailto:${c.email}`} className="text-indigo-700 hover:underline break-all">{c.email}</a>}
                  {c.phone && <a href={`tel:${c.phone}`} className="text-indigo-700 hover:underline">{c.phone}</a>}
                </p>
                {c.services_offered && <p className="text-[12px] text-slate-500 mt-0.5 line-clamp-2">{c.services_offered}</p>}
              </div>
              {canManage && (
                <RowMenu label={`Actions for ${c.organisation}`} items={[
                  { label: 'Edit', icon: PenLine, onClick: () => setEdit(c) },
                  c.is_active ? { label: 'Retire', icon: Ban, danger: true, onClick: () => setActive(c, false) } : { label: 'Restore', icon: RotateCcw, onClick: () => setActive(c, true) },
                ]} />
              )}
            </li>
          ))}
        </ul>
      )}
      {edit && <ContactModal contact={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); l.reload(); }} />}
    </Sheet>
  );
}

function ContactModal({ contact, onClose, onSaved }) {
  const [v, setV] = useState({
    organisation: contact.organisation || '', contact_person: contact.contact_person || '', email: contact.email || '', phone: contact.phone || '', services_offered: contact.services_offered || '',
  });
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));
  const badEmail = v.email.trim() && !isEmail(v.email);
  const badPhone = v.phone.trim() && !isPhone(v.phone);
  return (
    <ActionModal title={contact.id ? 'Edit referral contact' : 'Add referral contact'} confirmLabel={contact.id ? 'Save contact' : 'Add contact'} width="md"
      disabled={!v.organisation.trim() || badEmail || badPhone} onClose={onClose}
      onConfirm={async () => {
        const body = Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x.trim() || null]));
        if (contact.id) await api.put(`${BASE}/contacts/${contact.id}`, body); else await api.post(`${BASE}/contacts`, body);
        toast.success(contact.id ? 'Contact saved' : `${v.organisation.trim()} added`); onSaved(); return true;
      }}>
      <Field label="Organisation" required><Input value={v.organisation} maxLength={200} onChange={set('organisation')} aria-required="true" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Contact person"><Input value={v.contact_person} maxLength={200} onChange={set('contact_person')} /></Field>
        <Field label="Phone" error={badPhone ? 'This isn’t a valid phone number.' : undefined}><Input type="tel" value={v.phone} onChange={set('phone')} /></Field>
      </div>
      <Field label="Email" error={badEmail ? 'This isn’t a valid email address.' : undefined}><Input type="email" value={v.email} onChange={set('email')} /></Field>
      <Field label="Services offered"><Textarea rows={3} value={v.services_offered} maxLength={5000} onChange={set('services_offered')} placeholder="For example: OT assessments, home modifications, northern suburbs" /></Field>
    </ActionModal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Online form fields editor (6.7: Admin can edit the form fields). Also placed under Settings.
// ═══════════════════════════════════════════════════════════════════════════════════════════════
const FORM_TABS = [
  { value: 'referral_details', label: 'Referral Details', hint: 'Sent to referrers for missing details' },
  { value: 'referral_response', label: 'Referral Response', hint: 'Sent to providers with a referral' },
];
const KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;
let rowSeq = 0;
const slugKey = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^[^a-z]+/, '').replace(/_+$/, '').slice(0, 40);
function rowsFrom(cfg) {
  if (!cfg) return [];
  const std = new Set((cfg.defaults || []).map((d) => d.key));
  const core = new Set(cfg.core || []);
  return (cfg.fields || []).map((f) => {
    rowSeq += 1;
    return {
      _id: `fr-${rowSeq}`, key: f.key, label: f.label || '', type: f.type, required: !!f.required, help: f.help || '',
      options: f.options || null, optionsText: (f.options || []).map((o) => o.label || o.value).join('\n'),
      required_if: f.required_if || null, std: std.has(f.key), core: core.has(f.key), isNew: false, keyTouched: true,
    };
  });
}
function optionsFromText(row) {
  const old = row.options || [];
  return row.optionsText.split('\n').map((s) => s.trim()).filter(Boolean).map((label) => {
    const prev = old.find((o) => (o.label || o.value) === label);
    return prev ? { value: prev.value, label } : { value: label.slice(0, 80), label };
  });
}
function rowPayload(row) {
  const out = { key: row.key.trim(), label: row.label.trim(), required: !!row.required };
  if (row.help.trim()) out.help = row.help.trim();
  if (!row.std) {
    out.type = row.type;
    if (row.type === 'select') out.options = optionsFromText(row);
  }
  return out;
}

export function ReferralFormFields({ canAdmin: canAdminProp, bare = false }) {
  const meL = useLoad('/sc/me', null, { skip: canAdminProp !== undefined });
  const canAdmin = canAdminProp !== undefined ? !!canAdminProp : (meL.data?.permissions || []).includes('admin');
  const l = useLoad(`${BASE}/form-fields`);
  const [formType, setFormType] = useState('referral_details');
  const cfg = l.data?.forms?.[formType];
  const baseline = useMemo(() => rowsFrom(cfg), [cfg]);
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  useEffect(() => { setRows(baseline); setErrors([]); }, [baseline]);
  const fieldTypes = l.data?.field_types || Object.keys(FIELD_TYPE_LABELS);
  const dirty = JSON.stringify(rows.map(rowPayload)) !== JSON.stringify(baseline.map(rowPayload));
  const missingStd = (cfg?.defaults || []).filter((d) => !rows.some((r) => r.key === d.key));

  const patch = (rid, p) => setRows((rs) => rs.map((r) => {
    if (r._id !== rid) return r;
    const n = { ...r, ...p };
    if (n.isNew && !n.keyTouched && p.label !== undefined) n.key = slugKey(p.label);
    return n;
  }));
  const move = (i, dir) => setRows((rs) => {
    const j = i + dir;
    if (j < 0 || j >= rs.length) return rs;
    const n = [...rs];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const remove = (rid) => setRows((rs) => rs.filter((r) => r._id !== rid));
  const addCustom = () => { rowSeq += 1; setRows((rs) => [...rs, { _id: `fr-${rowSeq}`, key: '', label: '', type: 'text', required: false, help: '', options: null, optionsText: '', std: false, core: false, isNew: true, keyTouched: false }]); };
  const addStd = (key) => {
    const d = (cfg?.defaults || []).find((x) => x.key === key);
    if (!d) return;
    rowSeq += 1;
    setRows((rs) => [...rs, { _id: `fr-${rowSeq}`, key: d.key, label: d.label, type: d.type, required: !!d.required, help: d.help || '', options: d.options || null, optionsText: '', required_if: d.required_if || null, std: true, core: (cfg.core || []).includes(d.key), isNew: false, keyTouched: true }]);
  };
  const switchForm = (t) => {
    if (t === formType) return;
    if (dirty) { toast.error('Save or discard your changes first.'); return; }
    setFormType(t);
  };
  const check = () => {
    const errs = [];
    const seen = new Set();
    rows.forEach((r) => {
      const name = r.label.trim() || r.key || 'A new field';
      if (!r.label.trim()) errs.push(`${name} needs a label.`);
      if (!KEY_RE.test(r.key)) errs.push(`${name} needs a key: lower-case letters, numbers and underscores, starting with a letter.`);
      else if (seen.has(r.key)) errs.push(`The key ${r.key} is used twice.`);
      seen.add(r.key);
      if (!r.std && r.type === 'select' && !optionsFromText(r).length) errs.push(`${name} needs at least one option.`);
    });
    return errs;
  };
  const save = async () => {
    const errs = check();
    setErrors(errs);
    if (errs.length) return;
    setBusy(true);
    try {
      const res = await api.put(`${BASE}/form-fields/${formType}`, { fields: rows.map(rowPayload) });
      l.setData((dd) => ({ ...dd, forms: { ...dd.forms, [formType]: { ...dd.forms[formType], fields: res.data?.fields || [] } } }));
      toast.success(`${FORM_TABS.find((t) => t.value === formType).label} form saved`);
    } catch (e) {
      const list = e?.response?.data?.errors;
      setErrors(Array.isArray(list) && list.length ? list : [errMsg(e)]);
    } finally { setBusy(false); }
  };
  const reset = async () => {
    const res = await api.put(`${BASE}/form-fields/${formType}`, { reset: true });
    l.setData((dd) => ({ ...dd, forms: { ...dd.forms, [formType]: { ...dd.forms[formType], fields: res.data?.fields || [] } } }));
    toast.success('Form reset to the standard fields');
    return true;
  };

  const content = l.error ? <ErrorBlock error={l.error} onRetry={l.reload} /> : !l.data ? <LoadingBlock /> : (
    <div className="space-y-4">
      {!canAdmin && <Banner tone="amber" title="Read only">Only users with the Admin permission can change the form fields.</Banner>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="group" aria-label="Choose a form">
          <Segmented value={formType} onChange={switchForm} options={FORM_TABS.map((t) => ({ value: t.value, label: t.label }))} />
        </div>
        <p className="text-[12.5px] text-slate-500">{FORM_TABS.find((t) => t.value === formType).hint}. Links already sent keep the questions they were sent with.</p>
      </div>
      {errors.length > 0 && (
        <Banner tone="red" title="These fields need fixing">
          <ul className="list-disc pl-4 space-y-0.5">{errors.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </Banner>
      )}
      <ol className="space-y-2.5">
        {rows.map((r, i) => (
          <FieldRow key={r._id} row={r} index={i} count={rows.length} fieldTypes={fieldTypes} disabled={!canAdmin}
            onPatch={(p) => patch(r._id, p)} onMove={(dir) => move(i, dir)} onRemove={() => remove(r._id)} />
        ))}
      </ol>
      {canAdmin && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" icon={Plus} onClick={addCustom}>Add a question</Button>
          {missingStd.length > 0 && (
            <LabelledSelect label="Add a standard field back" className="w-64">
              {(sid) => <Select key={missingStd.length} id={sid} value="" onChange={addStd} placeholder="Add a standard field back…" className={READABLE_PLACEHOLDER} options={missingStd.map((d) => ({ value: d.key, label: d.label }))} />}
            </LabelledSelect>
          )}
        </div>
      )}
      {canAdmin && (
        <div className="sticky bottom-0 -mx-1 px-1 py-3 bg-white/95 backdrop-blur border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
          <Button variant="ghost" icon={RotateCcw} onClick={() => setResetOpen(true)}>Reset to standard fields</Button>
          <div className="flex items-center gap-2">
            {dirty && <span className="text-[12px] text-amber-700 font-medium">Unsaved changes</span>}
            {dirty && <Button variant="ghost" onClick={() => { setRows(baseline); setErrors([]); }}>Discard</Button>}
            <Button onClick={save} loading={busy} disabled={!dirty}>Save form</Button>
          </div>
        </div>
      )}
      {resetOpen && (
        <ConfirmModal open title={`Reset the ${FORM_TABS.find((t) => t.value === formType).label} form?`} confirmLabel="Reset form" danger
          onClose={() => setResetOpen(false)} onConfirm={reset}>
          Custom questions, labels, help text and order on this form are removed. Links already sent are not changed.
        </ConfirmModal>
      )}
    </div>
  );
  if (bare) return content;
  return (
    <Panel title="Online referral forms" icon={FileText} sub="The questions on the forms referrers and providers fill in. Fields the system reads can be relabelled and moved, but not removed.">
      {content}
    </Panel>
  );
}

function FieldRow({ row, index, count, fieldTypes, disabled, onPatch, onMove, onRemove }) {
  const name = row.label || row.key || 'new question';
  const lockedType = row.std;
  return (
    <li className={clsx('rounded-xl ring-1 ring-inset bg-white p-3', row.core ? 'ring-violet-200/80' : 'ring-slate-200/80')}>
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-1 pt-6">
          <button type="button" disabled={disabled || index === 0} onClick={() => onMove(-1)} aria-label={`Move ${name} up`}
            className="p-2 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
            <MoveUp size={14} aria-hidden />
          </button>
          <button type="button" disabled={disabled || index === count - 1} onClick={() => onMove(1)} aria-label={`Move ${name} down`}
            className="p-2 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
            <MoveDown size={14} aria-hidden />
          </button>
        </div>
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[1fr_160px] gap-3">
          <Field label="Question" required><Input value={row.label} maxLength={200} disabled={disabled} onChange={(e) => onPatch({ label: e.target.value })} /></Field>
          {lockedType ? (
            <div>
              <p className="text-[12.5px] font-semibold text-slate-700 mb-1.5">Answer type</p>
              <p className="h-10 flex items-center text-[13px] text-slate-600">{FIELD_TYPE_LABELS[row.type] || words(row.type)}</p>
            </div>
          ) : (
            <Field label="Answer type">
              <Select value={row.type} disabled={disabled} onChange={(t) => onPatch({ type: t })} options={fieldTypes.map((t) => ({ value: t, label: FIELD_TYPE_LABELS[t] || words(t) }))} />
            </Field>
          )}
          {row.isNew && (
            <Field label="Key" hint="Lower-case letters, numbers and underscores. Answers are saved under it.">
              <Input value={row.key} maxLength={40} disabled={disabled} className="font-mono text-[12.5px]" onChange={(e) => onPatch({ key: e.target.value.toLowerCase(), keyTouched: true })} />
            </Field>
          )}
          <Field label="Help text" className={row.isNew ? undefined : 'sm:col-span-2'}><Input value={row.help} maxLength={500} disabled={disabled} placeholder="Optional. Shown under the question." onChange={(e) => onPatch({ help: e.target.value })} /></Field>
          {!row.std && row.type === 'select' && (
            <Field label="Choices" hint="One per line." className="sm:col-span-2">
              <Textarea rows={3} value={row.optionsText} disabled={disabled} onChange={(e) => onPatch({ optionsText: e.target.value })} />
            </Field>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 pt-6 w-[118px] flex-shrink-0">
          <Checkbox label="Required" checked={row.required} disabled={disabled || row.core} onChange={(x) => onPatch({ required: x })} />
          {row.core ? <Pill tone="violet" label="Needed by Rostery" /> : !disabled && (
            <Button size="sm" variant="ghost" icon={Trash2} onClick={onRemove} aria-label={`Remove ${name}`} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">Remove</Button>
          )}
        </div>
      </div>
      <p className="mt-2 pl-8 text-[12px] text-slate-500">
        <span className="font-mono">{row.key || 'key not set'}</span>
        {row.std ? ' · standard field' : ' · your question'}
        {row.required_if ? ' · required when the provider declines' : ''}
      </p>
    </li>
  );
}
